import { Component, OnInit, inject } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Driver } from "../../../fleet/driver/driver.model";
import { Vehicle } from "../../../fleet/vehicle/vehicle.model";
import { DriverService } from "../../../fleet/driver/driver.service";
import { VehicleService } from "../../../fleet/vehicle/vehicle.service";
import { SessionStorageService } from "../../../services/session-storage.service";
import { ShipmentService } from "../../shipment.service";
import { Shipment } from "../../models/shipment";
import { AbstractControl, FormGroup, FormBuilder, FormArray, ValidationErrors, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import * as XLSX from 'xlsx';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { Observable, catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { ShipmentDelivery } from '../../models/delivery/shipment-delivery.model';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { DialogModule } from 'primeng/dialog';
import { EditDriverComponent } from 'src/app/fleet/driver/edit-driver/edit-driver.component';
import { EditVehicleComponent } from 'src/app/fleet/vehicle/edit-vehicle/edit-vehicle.component';
import { FlexFieldFormComponent } from 'src/app/shared/flex-fields/flex-field-form/flex-field-form.component';
import { FlexFieldConfig, FlexFieldValue } from 'src/app/shared/flex-fields/flex-field.model';
import { FlexFieldService } from 'src/app/shared/flex-fields/flex-field.service';
import { FleetAvailabilityResponse, FleetAvailabilityRow, Mobilization, MobilizationSearchParams } from 'src/app/fleet/mobilization/models/mobilization.model';
import { MobilizationService } from 'src/app/fleet/mobilization/mobilization.service';
import { PayloadItemView } from 'src/app/models/meta-params/payload-item-view';
import { ShipmentDeliveryType } from '../../models/delivery/shipment-delivery-type.model';
import { HrService } from 'src/app/hr/hr.service';
import { HrWorkforceReadinessRuleResult, HrWorkforceReadinessSnapshot } from 'src/app/hr/hr.models';
import { APIListWrapper } from 'src/app/models/common/api-list-wrapper.model';
import { Company } from 'src/app/company/company.model';
import { TenantScopeService } from 'src/app/company/tenant-scope.service';
import { LocaleService } from 'src/app/i18n/locale.service';
import { fleetResourceAgencyLabel, fleetResourceOwnerLabel, fleetResourceOwnershipSearchText, fleetResourceOwnershipTypeLabel, fleetResourceSourceCompanyLabel } from 'src/app/shared/utils/fleet-resource-ownership.util';
import { isSisterCompanyResource, resolveSisterCompanyScope, sisterCompanyFleetAccessMessage, SisterCompanyScope } from 'src/app/shared/utils/sister-company-scope.util';
import { QuickFleetOnboardingDialogComponent, QuickFleetOnboardingResult } from 'src/app/fleet/quick-fleet-onboarding/quick-fleet-onboarding-dialog.component';
import { CrmService, TransporterFleetOption } from 'src/app/crm/crm.service';
import { FieldWorkService } from 'src/app/field-work/field-work.service';
import { FieldLaborCandidate, FieldLaborRequirement, FieldWorkPackage, FieldWorker } from 'src/app/field-work/field-work.models';
import { PartyQuickCreateDialogComponent, PartyQuickCreateResult } from 'src/app/shared/party-quick-create/party-quick-create-dialog.component';
import { Laborer } from 'src/app/field-work/laborer.model';
import { DeliveryCashBoxOption, PettyCashService, preferredDeliveryCashBoxId } from 'src/app/accounts/workforce-cash/petty-cash.service';
import { WorkforceCashService } from 'src/app/accounts/workforce-cash/workforce-cash.service';
import { ShipmentContainerOption } from '../../shipment.service';

type DeliveryImportStatus = 'VALID' | 'INVALID';

interface DeliveryImportColumn {
    key: string;
    label: string;
}

interface DeliveryImportRow {
    rowNumber: number;
    values: Record<string, string>;
    status: DeliveryImportStatus;
    errors: string[];
    driver?: Driver | null;
    vehicle?: Vehicle | null;
    payloadQuantities: number[];
    flexFieldValues: FlexFieldValue[];
}

interface DeliveryImportLookupResult<T> {
    value: T | null;
    error?: string;
}

@Component({
    selector: 'app-generate-deliveries',
    templateUrl: './generate-deliveries.component.html',
    styleUrls: ['./generate-deliveries.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ToastModule,
        ProgressBarModule,
        BreadcrumbModule,
        TableModule,
        ButtonModule,
        MenuModule,
        SelectModule,
        MultiSelectModule,
        SelectButtonModule,
        ToggleSwitchModule,
        InputTextModule,
        InputNumberModule,
        TagModule,
        CheckboxModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        DialogModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        EditDriverComponent,
        EditVehicleComponent,
        QuickFleetOnboardingDialogComponent,
        FlexFieldFormComponent,
        PartyQuickCreateDialogComponent
    ],
    providers: [
        MessageService,
        ShipmentService,
        DriverService,
        VehicleService,
        SessionStorageService
    ]
})
export class GenerateDeliveriesComponent implements OnInit {
    readonly legacyWaybillFlexFieldNames = [
        'waybillNumber', 'waybillNo', 'awbNumber', 'awbNo',
        'deliveryWaybillNumber', 'deliveryWaybillNo'
    ];


    isLoading: boolean = false;
    driverOptionsLoading: boolean = false;
    vehicleOptionsLoading: boolean = false;
    vehicleOptionsError = false;
    deliveryGenerationInProgress: boolean = false;
    driverDialogVisible: boolean = false;
    vehicleDialogVisible: boolean = false;
    quickFleetOnboardingVisible: boolean = false;
    private quickFleetAssignmentIndex: number | null = null;
    mobilizationDialogVisible: boolean = false;
    deliveryImportDialogVisible: boolean = false;
    deliveryImportLoading: boolean = false;
    assignmentSource: 'owned' | 'freelance' | 'rental' | 'transporter' = 'owned';
    resourceView: 'current' | 'sister' = 'current';
    sisterCompanyScope: SisterCompanyScope | null = null;
    selectedSisterCompanyId: number | null = null;
    shipmentId: number;
    shipment: Shipment = {} as Shipment;
    shipmentContainers: ShipmentContainerOption[] = [];
    containersLoading = false;
    workforcePreview?: FieldWorkPackage;
    laborerQuickCreateVisible = false;
    private laborerQuickCreateAssignmentIndex: number | null = null;
    private laborerQuickCreateSlotIndex: number | null = null;

    // Keep a real form available from construction. Angular can evaluate the
    // template before ngOnInit completes, particularly after a stale-build
    // recovery or direct route load.
    deliveryForm: FormGroup;
    availableDrivers: Driver[] = [];
    availableVehicles: Vehicle[] = [];
    private vehicleSelectionMessages = new WeakMap<FormGroup, string>();
    private vehicleSelectionVersions = new WeakMap<FormGroup, number>();
    transporterOptions: TransporterFleetOption[] = [];
    transporterOptionsLoading = false;
    selectedTransporterCrmClientId: number | null = null;
    mobilizations: Mobilization[] = [];
    mobilizationsLoading: boolean = false;
    mobilizationsTotalRecords: number = 0;
    mobilizationsFirst: number = 0;
    mobilizationsRowsPerPage: number = 10;
    mobilizationKeyword: string = '';
    deliveryFlexFieldConfigs: FlexFieldConfig[] = [];
    deliveryImportRows: DeliveryImportRow[] = [];
    deliveryImportFileName: string | null = null;
    driverReadinessByDriverId = new Map<number, HrWorkforceReadinessSnapshot>();
    driverReadinessLoadingByDriverId = new Set<number>();
    driverReadinessErrorsByDriverId = new Set<number>();
    driverAvailabilityByKey = new Map<string, FleetAvailabilityRow>();
    vehicleAvailabilityByKey = new Map<string, FleetAvailabilityRow>();
    private payloadAllocationsManuallyEdited = false;

    readonly resourceViewOptions = [
        { label: 'Current Company', value: 'current' },
        { label: 'Sister Companies', value: 'sister' }
    ];

    readonly cashPurposeOptions = [
        { label: 'No cash', value: 'NONE' },
        { label: 'Company expense float', value: 'COMPANY_EXPENSE_FLOAT' },
        { label: 'Advance against pay', value: 'ADVANCE_AGAINST_PAY' }
    ];
    readonly driverPayMethods = [{ label: 'No driver pay', value: 'NONE' }, { label: 'Fixed trip', value: 'FIXED_TRIP' }, { label: 'Origin to destination', value: 'CITY_PAIR_TRIP' }, { label: 'Per kilometre', value: 'PER_KM' }];
    readonly payDistanceSources = [{ label: 'Shipment route distance', value: 'PLANNED_ROUTE' }, { label: 'GPS device odometer', value: 'GPS_ODOMETER' }, { label: 'Start/end odometer photos', value: 'PHOTO_ODOMETER' }];
    cashBoxOptions: DeliveryCashBoxOption[] = [];
    cashBoxError = '';
    availableCashBoxes(currencyCode?: string): DeliveryCashBoxOption[] {
        return this.cashBoxOptions.filter(box => box.currencyCode === currencyCode);
    }

    readonly internalTransportCostSourceOptions = [
        { label: 'Own fleet internal cost', value: 'INTERNAL_TRANSPORT_COST' },
        { label: 'Sister fleet internal cost', value: 'SISTER_INTERNAL_TRANSPORT_COST' },
        { label: 'Manual internal cost', value: 'CUSTOM_INTERNAL_TRANSPORT_COST' }
    ];

    readonly externalTransportCostSourceOptions = [
        { label: 'Freelance carrier', value: 'FREELANCE' },
        { label: 'Rental vehicle', value: 'RENTAL' },
        { label: 'Transporter supplier', value: 'TRANSPORTER' },
        { label: 'Supplier rate / manual buying cost', value: 'SUPPLIER_DELIVERY_RATE_CARD' }
    ];

    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;
    private readonly localeService = inject(LocaleService);

    t(key: string, fallback: string): string {
        return this.localeService?.translateIfPresent(key, fallback) ?? fallback;
    }

    get assignmentActionItems(): MenuItem[] {
        return [
            {
                id: 'quickOnboard',
                label: this.t('pages.generateDeliveries.actions.quickOnboard', 'AI Quick Onboard Driver & Vehicle'),
                icon: 'pi pi-sparkles',
                command: () => this.openQuickFleetOnboarding()
            },
            {
                id: 'createDriver',
                label: this.t('pages.generateDeliveries.actions.createDriver', 'Create Driver'),
                icon: 'fa-solid fa-user-plus',
                command: () => this.openDriverDialog()
            },
            {
                id: 'createVehicle',
                label: this.t('pages.generateDeliveries.actions.createVehicle', 'Create Vehicle'),
                icon: 'fa-solid fa-truck-medical',
                command: () => this.openVehicleDialog()
            },
            {
                id: 'addMobilizedAssignment',
                label: this.t('pages.generateDeliveries.actions.addMobilizedAssignment', 'Add Mobilized Assignment'),
                icon: 'fa-solid fa-route',
                command: () => this.openMobilizationDialog()
            },
            {
                id: 'equalSplitPayload',
                label: this.t('pages.generateDeliveries.actions.equalSplitPayload', 'Equal Split Payload'),
                icon: 'fa-solid fa-scale-balanced',
                command: () => this.equalSplitPayloadAllocations()
            },
            {
                id: 'bulkUploadDeliveries',
                label: this.t('pages.generateDeliveries.actions.bulkUploadDeliveries', 'Bulk Upload Deliveries'),
                icon: 'pi pi-upload',
                command: () => this.openDeliveryImportDialog()
            },
            {
                id: 'downloadTemplate',
                label: this.t('pages.generateDeliveries.actions.downloadTemplate', 'Download Template'),
                icon: 'pi pi-download',
                command: () => this.downloadDeliveryImportTemplate()
            },
            {
                separator: true
            },
            {
                id: 'clearAll',
                label: this.t('pages.generateDeliveries.actions.clearAll', 'Clear All'),
                icon: 'fa-solid fa-xmark',
                command: () => this.clearSelections()
            }
        ];
    }

    private readonly optionPageSize = 50;
    private resourceQueryVersion = 0;
    private shipmentLoadRequestToken = 0;
    private scopedAvailabilityKey: string | null = null;
    private scopedAvailabilityLoadingKey: string | null = null;

    constructor(
        private driverService: DriverService,
        private vehicleService: VehicleService,
        private messageService: MessageService,
        private shipmentService: ShipmentService,
        private mobilizationService: MobilizationService,
        private hrService: HrService,
        private flexFieldService: FlexFieldService,
        private tenantScopeService: TenantScopeService,
        private crmService: CrmService,
        private fieldWorkService: FieldWorkService,
        private fb: FormBuilder,
        private router: Router, private route: ActivatedRoute,
        private sessionStorageService: SessionStorageService,
        private pettyCash: PettyCashService,
        private workforceCash: WorkforceCashService
    ) {
        this.deliveryForm = this.fb.group({
            assignments: this.fb.array([])
        });
    }

    ngOnInit(): void {
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        const sourceParam = this.route.snapshot.queryParamMap.get('source');
        const legacyFreelanceParam = this.route.snapshot.queryParamMap.get('isFreelancer') === 'true';
        this.assignmentSource = sourceParam === 'rental'
            ? 'rental'
            : sourceParam === 'transporter'
                ? 'transporter'
            : sourceParam === 'freelance' || legacyFreelanceParam
                ? 'freelance'
                : 'owned';

        this.route.params.subscribe(params => {
            this.shipmentId = Number(params['shipmentId']);
            this.breadcrumbItems = [{ label: 'Shipments', routerLink: '/shipment' },
            { label: `Shipment ${this.shipmentId}`, routerLink: ['/shipment/view', this.shipmentId] },
            { label: 'Generate Deliveries' }];
            this.loadShipmentForDirectRoute();
        });
    }

    private initializePage(shipment: Shipment): void {
        this.shipment = shipment;
        this.breadcrumbItems = [{ label: 'Shipments', routerLink: '/shipment' },
        { label: shipment.shipmentNumber || `Shipment ${this.shipmentId}`, routerLink: ['/shipment/view', this.shipmentId] },
        { label: 'Generate Deliveries' }];
        this.initDeliveryForm();
        this.loadCashBoxOptions();
        this.initializeSisterScope();
        if (this.assignmentSource === 'transporter') {
            this.loadTransporterOptions();
        }
        this.loadExistingDeliveries();
        const requestToken = this.shipmentLoadRequestToken;
        this.shipmentContainers = [];
        this.containersLoading = true;
        this.shipmentService.getShipmentContainers(this.shipmentId).pipe(finalize(() => {
            if (requestToken === this.shipmentLoadRequestToken) this.containersLoading = false;
        })).subscribe({
            next: containers => {
                if (requestToken === this.shipmentLoadRequestToken)
                    this.shipmentContainers = containers.filter(container => !container.deliveryId)
                        .map(container => ({ ...container, containerNumber: container.containerNumber || container.caseNumber }));
            },
            error: () => {
                if (requestToken === this.shipmentLoadRequestToken)
                    this.messageService.add({ severity: 'error', summary: 'Containers unavailable', detail: 'Shipment containers could not be loaded.' });
            }
        });
        this.loadDeliveryFlexFieldConfigs();
        this.loadWorkforcePreview();
    }

    private loadShipmentForDirectRoute(): void {
        const requestedShipmentId = this.shipmentId;
        const requestToken = ++this.shipmentLoadRequestToken;
        if (!Number.isFinite(requestedShipmentId) || requestedShipmentId <= 0) {
            this.messageService.add({
                severity: 'error',
                summary: 'Invalid shipment',
                detail: 'A valid shipment is required to generate deliveries.'
            });
            this.router.navigate(['/shipment']);
            return;
        }

        this.isLoading = true;
        this.shipmentService.getShipmentById(requestedShipmentId, 'en-US')
            .pipe(finalize(() => {
                if (requestToken === this.shipmentLoadRequestToken) {
                    this.isLoading = false;
                }
            }))
            .subscribe({
                next: shipment => {
                    if (requestToken !== this.shipmentLoadRequestToken || requestedShipmentId !== this.shipmentId) {
                        return;
                    }
                    if (!shipment) {
                        this.handleShipmentLoadFailure();
                        return;
                    }
                    this.initializePage(shipment);
                },
                error: () => {
                    if (requestToken === this.shipmentLoadRequestToken && requestedShipmentId === this.shipmentId) {
                        this.handleShipmentLoadFailure();
                    }
                }
            });
    }

    private loadCashBoxOptions(): void {
        const companyId = this.shipment?.owningCompany?.id ?? this.currentCompanyId;
        if (!companyId) return;
        this.cashBoxError = '';
        this.pettyCash.deliveryBoxOptions(companyId).subscribe({
            next: boxes => {
                this.cashBoxOptions = boxes;
                this.assignments.controls.forEach(control => {
                    const money = control.get('tripMoney');
                    if (!money?.get('pettyCashBoxId')?.value)
                        money?.get('pettyCashBoxId')?.setValue(preferredDeliveryCashBoxId(boxes,
                            this.shipment?.sourceLocation?.id, money.get('currencyCode')?.value));
                });
            },
            error: () => this.cashBoxError = 'Cash boxes could not be loaded. Retry before requesting driver cash.'
        });
    }

    private handleShipmentLoadFailure(): void {
        this.messageService.add({
            severity: 'error',
            summary: 'Shipment unavailable',
            detail: 'The shipment could not be loaded for delivery generation.'
        });
        this.router.navigate(['/shipment']);
    }

    get currentCompanyId(): number | null {
        return this.sessionStorageService.getCompany()?.id ?? null;
    }

    get currentCompanyName(): string | null {
        return this.sessionStorageService.getCompany()?.name ?? null;
    }

    get sisterCompanies(): Company[] {
        return this.sisterCompanyScope?.sisterCompanies ?? [];
    }

    get canUseSisterCompanyMode(): boolean {
        return !!this.sisterCompanyScope;
    }

    get isSisterMode(): boolean {
        return this.resourceView === 'sister' && this.canUseSisterCompanyMode;
    }

    get sisterCompanyAllLabel(): string {
        return 'All sister companies';
    }

    get visibleAssignmentActionItems(): MenuItem[] {
        if (!this.isSisterMode) {
            return this.assignmentActionItems;
        }
        const excludedIds = ['createDriver', 'createVehicle', 'quickOnboard', 'addMobilizedAssignment'];
        const excludedLabels = ['Create Driver', 'Create Vehicle', 'AI Quick Onboard Driver & Vehicle', 'Add Mobilized Assignment'];
        return this.assignmentActionItems.filter(item =>
            !excludedIds.includes(item.id ?? '') && !excludedLabels.includes(item.label ?? '')
        );
    }

    onResourceViewChange(value: 'current' | 'sister'): void {
        const previousView = this.resourceView;
        this.resourceView = value === 'sister' && this.canUseSisterCompanyMode ? 'sister' : 'current';
        if (!this.isSisterMode) {
            this.selectedSisterCompanyId = null;
        }
        if (previousView !== this.resourceView) this.clearScopedAssignments();
        this.syncRouteState();
        this.loadAssignmentResources();
    }

    onSisterCompanyFilterChange(value: number | null): void {
        const previousCompanyId = this.selectedSisterCompanyId;
        this.selectedSisterCompanyId = this.isValidSisterCompanyId(value) ? value : null;
        if (previousCompanyId !== this.selectedSisterCompanyId) this.clearScopedAssignments();
        this.syncRouteState();
        this.loadAssignmentResources();
    }

    private clearScopedAssignments(): void {
        this.assignments.controls.forEach(group => group.patchValue({ mobilizationId: null, driver: null, vehicle: null }));
    }

    onAssignmentTransporterChange(index: number, transporterCrmClientId: number | null): void {
        const normalizedId = transporterCrmClientId ? Number(transporterCrmClientId) : null;
        const group = this.assignments.at(index);
        group?.patchValue({
            transporterCrmClientId: normalizedId,
            driver: null,
            vehicle: null
        }, { emitEvent: false });
    }

    initDeliveryForm(): void {
        this.deliveryForm = this.fb.group({
            assignments: this.fb.array([])
        });
    }

    get assignments(): FormArray {
        const assignments = this.deliveryForm?.get('assignments');
        return assignments instanceof FormArray ? assignments : this.fb.array([]);
    }

    createAssignmentGroup(): FormGroup {
        const group = this.fb.group({
            mobilizationId: [null],
            containerCaseIds: [[]],
            assignmentType: [null],
            transporterCrmClientId: [null],
            driver: [null, [Validators.required, this.registeredDriverValidator]],
            vehicle: [null, Validators.required],
            // The API resolves the channel from the driver snapshot. Dispatch
            // confirms one neutral transport cost instead of selecting a source.
            transportCostAmount: [null, [Validators.required, Validators.min(0)]],
            transportCostCurrencyCode: [this.shipment?.currencyCode || 'SAR'],
            waybillNumber: [''],
            tripMoney: this.fb.group({
                enabled: [true],
                payMethod: ['NONE'],
                quotedDriverRate: [null as number | null],
                ratePerKm: [null as number | null],
                distanceSource: ['PHOTO_ODOMETER'],
                expectedStartPlace: [''], expectedParkingPlace: [''], payTripGroupKey: [''],
                tripEarningAmount: [null, Validators.min(0.01)],
                cashPurpose: ['NONE'],
                tripCashAmount: [null, Validators.min(0.01)],
                pettyCashBoxId: [preferredDeliveryCashBoxId(this.cashBoxOptions, this.shipment?.sourceLocation?.id,
                    this.shipment?.currencyCode || 'SAR')],
                currencyCode: [this.shipment?.currencyCode || 'SAR'],
                reason: ['']
            }, { validators: (control: AbstractControl): ValidationErrors | null => {
                const money = control.value;
                const hasOverride = money.tripEarningAmount != null
                    || (money.cashPurpose !== 'NONE' && money.tripCashAmount != null);
                if (money.enabled !== false && money.payMethod === 'PER_KM' && !money.distanceSource)
                    return { driverRateRequired: true };
                if (money.enabled !== false && money.cashPurpose !== 'NONE' && !money.pettyCashBoxId)
                    return { cashBoxRequired: true };
                return money.enabled !== false && hasOverride && !money.reason?.trim()
                    ? { overrideReasonRequired: true } : null;
            } }),
            payloadItems: this.createPayloadAllocationArray(),
            flexFields: this.fb.array([]),
            laborSelections: this.createLaborSelectionArray()
        });

        group.get('driver')?.valueChanges.subscribe(driver => {
            const selectedDriver = driver as Driver | null;
            this.resolveAssignmentTypeFromDriver(group, selectedDriver);
            this.evaluateAssignmentDriverReadiness(selectedDriver);
            this.preferDriverForLabor(group, selectedDriver);
            this.selectCurrentVehicleForDriver(group, selectedDriver);
            this.loadDriverPayQuote(group);
        });
        group.get('tripMoney.payMethod')?.valueChanges.subscribe(() => this.loadDriverPayQuote(group));
        group.get('vehicle')?.valueChanges.subscribe(vehicle => {
            if (vehicle) {
                this.vehicleSelectionMessages.delete(group);
            }
        });

        return group;
    }

    private loadDriverPayQuote(group: FormGroup): void {
        const driver = group.get('driver')?.value as Driver | null;
        const method = group.get('tripMoney.payMethod')?.value;
        const companyId = this.currentCompanyId;
        group.get('tripMoney.quotedDriverRate')?.setValue(null, { emitEvent: false });
        if (!driver?.id || !companyId || !this.shipmentId || method === 'NONE') return;
        this.workforceCash.driverRateQuote(companyId, this.shipmentId, driver.id, method,
            group.get('tripMoney.currencyCode')?.value || this.shipment?.currencyCode || 'SAR').subscribe({
            next: quote => { if (group.get('driver')?.value?.id !== driver.id || group.get('tripMoney.payMethod')?.value !== method) return;
                group.get('tripMoney.quotedDriverRate')?.setValue(quote.payRate, { emitEvent: false });
                if (method === 'PER_KM' && group.get('tripMoney.ratePerKm')?.value == null)
                group.get('tripMoney.ratePerKm')?.setValue(quote.payRate); },
            error: () => this.messageService.add({ severity: 'warn', summary: 'Driver rate missing',
                detail: 'Configure a matching driver rate card. Dispatch may continue, but Accounts posting will wait for finance review.' })
        });
    }

    private loadWorkforcePreview(): void {
        if (!this.shipmentId) return;
        this.fieldWorkService.shipmentWorkforcePreview(this.shipmentId).subscribe({
            next: preview => {
                this.workforcePreview = preview;
                this.assignments.controls.forEach(group => this.resetLaborSlots(group));
            },
            // Workforce remains optional: an unavailable preview must never hold up dispatch.
            error: () => this.workforcePreview = undefined
        });
    }

    workforceRequirements(): FieldLaborRequirement[] {
        return this.workforcePreview?.laborRequirements ?? [];
    }

    getLaborSelections(assignmentIndex: number): FormArray {
        return this.assignments.at(assignmentIndex).get('laborSelections') as FormArray;
    }

    laborCandidates(roleId?: number, assignmentIndex?: number, slotIndex?: number): FieldLaborCandidate[] {
        const candidates = this.workforceRequirements().find(row => row.fieldRoleId === roleId)?.candidates ?? [];
        if (assignmentIndex == null || slotIndex == null) return candidates.filter(candidate => candidate.eligible);
        const usedWorkerIds = new Set(this.getLaborSelections(assignmentIndex).controls
            .filter((_, index) => index !== slotIndex)
            .map(slot => slot.get('fieldWorkerId')?.value)
            .filter(Boolean));
        return candidates.filter(candidate => candidate.eligible
            && !usedWorkerIds.has(candidate.fieldWorkerId));
    }

    private createLaborSelectionArray(): FormArray {
        const controls: FormGroup[] = [];
        this.workforceRequirements().forEach(requirement => {
            const count = Math.max(0, Number(requirement.headcount ?? 0));
            for (let slot = 1; slot <= count; slot++) controls.push(this.createLaborSelectionGroup(requirement, slot));
        });
        return this.fb.array(controls);
    }

    private createLaborSelectionGroup(requirement: FieldLaborRequirement, slotNumber: number): FormGroup {
        return this.fb.group({
            fieldRoleId: [requirement.fieldRoleId],
            roleName: [requirement.roleName],
            slotNumber: [slotNumber],
            fieldWorkerId: [null],
            buyRateSnapshot: [null, Validators.min(0)],
            billingUnit: [requirement.billingUnit || 'TASK'],
            currencyCode: [requirement.currencyCode || this.shipment?.currencyCode || 'SAR'],
            overrideReason: ['']
        });
    }

    private resetLaborSlots(assignment: AbstractControl): void {
        const slots = assignment.get('laborSelections') as FormArray | null;
        if (!slots) return;
        slots.clear();
        this.createLaborSelectionArray().controls.forEach(control => slots.push(control));
        this.preferDriverForLabor(assignment as FormGroup, assignment.get('driver')?.value as Driver | null);
    }

    private preferDriverForLabor(assignment: FormGroup, driver: Driver | null): void {
        if (!driver?.id) return;
        const slots = assignment.get('laborSelections') as FormArray | null;
        if (!slots) return;
        const selected = new Set(slots.controls.map(slot => slot.get('fieldWorkerId')?.value).filter(Boolean));
        const preferred = this.workforceRequirements()
            .slice().sort((left, right) => (right.skills?.length ?? 0) - (left.skills?.length ?? 0))
            .map(requirement => ({
                requirement,
                candidate: (requirement.candidates ?? []).find(candidate =>
                    candidate.driverId === driver.id && !selected.has(candidate.fieldWorkerId)
                    && !(candidate.blockers ?? []).some(blocker => /^(IDENTITY|FINANCE|CAPABILITY):/.test(blocker))
                )
            }))
            .find(match => !!match.candidate);
        if (!preferred?.candidate?.fieldWorkerId) return;
        const slot = slots.controls.find(control => control.get('fieldRoleId')?.value === preferred.requirement.fieldRoleId
            && !control.get('fieldWorkerId')?.value);
        slot?.get('fieldWorkerId')?.setValue(preferred.candidate.fieldWorkerId, { emitEvent: false });
    }

    openLaborerQuickCreate(assignmentIndex: number, slotIndex: number): void {
        this.laborerQuickCreateAssignmentIndex = assignmentIndex;
        this.laborerQuickCreateSlotIndex = slotIndex;
        this.laborerQuickCreateVisible = true;
    }

    onLaborerQuickCreated(result: PartyQuickCreateResult): void {
        this.laborerQuickCreateVisible = false;
        const laborer = result.record as Laborer;
        const assignmentIndex = this.laborerQuickCreateAssignmentIndex;
        const slotIndex = this.laborerQuickCreateSlotIndex;
        this.laborerQuickCreateAssignmentIndex = null;
        this.laborerQuickCreateSlotIndex = null;
        if (!laborer?.id || assignmentIndex == null || slotIndex == null) return;
        const slot = this.getLaborSelections(assignmentIndex).at(slotIndex);
        const roleId = slot?.get('fieldRoleId')?.value;
        const workerCategory = this.workforceRequirements().find(requirement => requirement.fieldRoleId === roleId)
            ?.workerCategory || 'LABOR';
        this.fieldWorkService.savePerson({
            laborerId: laborer.id,
            sourceType: 'FREELANCE',
            workerCategory,
            homeLocationId: this.shipment?.sourceLocation?.id,
            currentLocationId: this.shipment?.sourceLocation?.id,
            currentLocationName: this.shipment?.sourceLocation?.name,
            status: 'ACTIVE'
        }).subscribe({
            next: (worker: FieldWorker) => {
                this.workforceRequirements().forEach(requirement => {
                    if (requirement.fieldRoleId === roleId) {
                        requirement.candidates = [...(requirement.candidates ?? []), {
                            fieldWorkerId: worker.fieldWorkerId,
                            displayName: worker.displayName,
                            sourceType: worker.sourceType,
                            eligible: false,
                            blockers: ['CAPABILITY: configure the required role capabilities before assigning this laborer.']
                        }];
                    }
                });
                this.messageService.add({ severity: 'success', summary: 'Laborer created', detail: 'The new laborer is available after required capabilities are configured.' });
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Laborer not linked', detail: error?.error?.message || 'The laborer was created but could not be linked to Field Work.' })
        });
    }

    assignmentUsesInternalCost(index: number): boolean {
        if (!this.assignments.at(index)?.get('driver')?.value) return false;
        const assignmentType = this.assignments.at(index)?.get('assignmentType')?.value as string | null;
        return assignmentType !== 'freelance' && assignmentType !== 'transporter';
    }

    assignmentCostLabel(index: number): string {
        if (!this.assignments.at(index)?.get('driver')?.value) return 'Select Driver';
        const assignmentType = this.assignments.at(index)?.get('assignmentType')?.value as string | null;
        if (assignmentType === 'freelance') {
            return 'Driver Payable';
        }
        return assignmentType === 'transporter' ? 'Transporter Payable' : 'Internal Cost';
    }

    assignmentCostMessage(index: number): string {
        if (!this.assignments.at(index)?.get('driver')?.value) return 'Select a driver to determine how the transport cost is recorded.';
        const assignmentType = this.assignments.at(index)?.get('assignmentType')?.value as string | null;
        if (assignmentType === 'freelance') {
            return 'This amount will create a payable to the selected freelance driver when the shipment is posted to Accounts.';
        }
        if (assignmentType === 'transporter') {
            return 'This amount will create a payable to the selected transporter when the shipment is posted to Accounts.';
        }
        return 'This amount will create internal bulk cost when the shipment is posted to Accounts.';
    }

    private registeredDriverValidator(control: AbstractControl): ValidationErrors | null {
        const driver = control.value as Driver | null;
        return driver && !driver.id ? { registeredDriver: true } : null;
    }

    getAssignmentFlexFields(index: number): FormArray {
        return this.assignments.at(index).get('flexFields') as FormArray;
    }

    hasDeliveryFlexFields(): boolean {
        return (this.deliveryFlexFieldConfigs ?? []).length > 0;
    }

    get deliveryFlexOwningCompanyId(): number | null {
        return this.shipment?.owningCompany?.id ?? this.sessionStorageService.getCompany()?.id ?? null;
    }

    assignmentCountStatusMessage(): string {
        const requestedTrucks = this.requestedTruckCount();
        const existingDeliveries = this.existingDeliveryCount();
        const addedAssignments = this.assignmentCount();
        const totalPlanned = existingDeliveries + addedAssignments;
        const existingDeliveryLabel = existingDeliveries === 1 ? 'existing delivery' : 'existing deliveries';
        const assignmentLabel = this.pluralize(addedAssignments, 'assignment');
        const countBreakdown = existingDeliveries > 0
            ? `${existingDeliveries} ${existingDeliveryLabel}, ${addedAssignments} ${assignmentLabel} added`
            : `${addedAssignments} ${assignmentLabel} added`;

        if (requestedTrucks <= 0) {
            return `Requested trucks not set; ${countBreakdown}.`;
        }

        const truckLabel = this.pluralize(requestedTrucks, 'truck');
        if (totalPlanned > requestedTrucks) {
            if (existingDeliveries === 0) {
                return `${requestedTrucks} ${truckLabel} requested, but ${addedAssignments} ${assignmentLabel} added.`;
            }
            return `${requestedTrucks} ${truckLabel} requested, but ${totalPlanned} total planned (${countBreakdown}).`;
        }

        if (totalPlanned === requestedTrucks) {
            return `${requestedTrucks} ${truckLabel} requested, ${countBreakdown}.`;
        }

        return `${requestedTrucks} ${truckLabel} requested, ${countBreakdown}. ${requestedTrucks - totalPlanned} remaining.`;
    }

    assignmentCountStatusClass(): string {
        return `assignment-count-banner--${this.assignmentCountStatusTone()}`;
    }

    assignmentCountStatusIcon(): string {
        switch (this.assignmentCountStatusTone()) {
            case 'warning':
                return 'fa-solid fa-triangle-exclamation';
            case 'success':
                return 'fa-solid fa-circle-check';
            default:
                return 'fa-solid fa-circle-info';
        }
    }

    assignmentCountStatusTone(): 'neutral' | 'success' | 'warning' {
        const requestedTrucks = this.requestedTruckCount();
        const totalPlanned = this.existingDeliveryCount() + this.assignmentCount();

        if (requestedTrucks > 0 && totalPlanned > requestedTrucks) {
            return 'warning';
        }

        if (requestedTrucks > 0 && totalPlanned === requestedTrucks) {
            return 'success';
        }

        return 'neutral';
    }

    addAssignment(): void {
        this.assignments.push(this.createAssignmentGroup());
        this.rebalancePayloadAllocationsIfAutomatic();
    }

    /** Driver ownership is the finance authority for a delivery. */
    private resolveAssignmentTypeFromDriver(group: FormGroup, driver: Driver | null): void {
        if (!driver) {
            group.get('assignmentType')?.setValue(null, { emitEvent: false });
            group.get('transporterCrmClientId')?.clearValidators();
            group.get('transporterCrmClientId')?.setValue(null, { emitEvent: false });
            group.get('transporterCrmClientId')?.updateValueAndValidity({ emitEvent: false });
            return;
        }

        const ownership = String(driver.resourceOwnershipType || '').trim().toUpperCase();
        const assignmentType = ownership === 'FREELANCE' || ownership === 'FREELANCER'
            ? 'freelance'
            : ownership === 'RENTAL'
                ? 'rental'
                : ownership === 'TRANSPORTER' || ownership === 'SUPPLIER'
                    ? 'transporter'
                    : 'owned';

        if (group.get('assignmentType')?.value !== assignmentType) {
            group.get('assignmentType')?.setValue(assignmentType, { emitEvent: false });
        }

        const transporterControl = group.get('transporterCrmClientId');
        if (assignmentType === 'transporter') {
            transporterControl?.setValidators(Validators.required);
            transporterControl?.setValue(driver.transporterCrmClientId ?? null, { emitEvent: false });
            if (!this.transporterOptions.length && !this.transporterOptionsLoading) {
                this.loadTransporterOptions();
            }
        } else {
            transporterControl?.clearValidators();
            transporterControl?.setValue(null, { emitEvent: false });
        }
        transporterControl?.updateValueAndValidity({ emitEvent: false });
    }

    assignmentTypeLabel(index: number): string {
        if (!this.assignments.at(index)?.get('driver')?.value) {
            return 'Fleet Assignment';
        }
        const type = this.assignments.at(index)?.get('assignmentType')?.value as string | null;
        switch (type) {
            case 'freelance': return 'Freelance Assignment';
            case 'rental': return 'Rental Assignment';
            case 'transporter': return 'Transporter Assignment';
            default: return 'Own Fleet Assignment';
        }
    }

    assignmentTypeTag(index: number): string {
        if (!this.assignments.at(index)?.get('driver')?.value) {
            return 'Select Driver';
        }
        const type = this.assignments.at(index)?.get('assignmentType')?.value as string | null;
        switch (type) {
            case 'freelance': return 'Freelance';
            case 'rental': return 'Rental';
            case 'transporter': return 'Transporter';
            default: return 'Own Fleet';
        }
    }

    assignmentTypeSeverity(index: number): 'success' | 'info' | 'warn' {
        if (!this.assignments.at(index)?.get('driver')?.value) return 'info';
        const type = this.assignments.at(index)?.get('assignmentType')?.value as string | null;
        return type === 'freelance' || type === 'transporter' ? 'info' : type === 'rental' ? 'warn' : 'success';
    }

    removeAssignment(index: number): void {
        this.assignments.removeAt(index);
        this.rebalancePayloadAllocationsIfAutomatic();
    }

    loadDeliveryFlexFieldConfigs(): void {
        this.flexFieldService.getConfigs('SHIPMENT_DELIVERY', this.deliveryFlexOwningCompanyId ?? undefined)
            .subscribe({
                next: configs => this.deliveryFlexFieldConfigs = (configs ?? [])
                    .filter(config => !this.isLegacyWaybillFlexField(config)),
                error: () => this.deliveryFlexFieldConfigs = []
            });
    }

    private isLegacyWaybillFlexField(config: FlexFieldConfig): boolean {
        const normalize = (value?: string | null) => (value ?? '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        const legacyNames = this.legacyWaybillFlexFieldNames.map(normalize);
        return legacyNames.includes(normalize(config.fieldName))
            || legacyNames.includes(normalize(config.alternateFieldName));
    }

    private loadExistingDeliveries(): void {
        this.shipmentService.getDeliveriesForShipment(this.shipmentId).subscribe({
            next: deliveries => {
                if (!this.shipment) {
                    return;
                }
                this.shipment.deliveries = deliveries ?? [];
                this.rebalancePayloadAllocationsIfAutomatic();
                this.clampPayloadAllocationsToAvailable();
            },
            error: () => {
                if (this.shipment) {
                    this.shipment.deliveries = this.shipment.deliveries ?? [];
                }
            }
        });
    }

    openDeliveryImportDialog(): void {
        this.deliveryImportDialogVisible = true;
        this.deliveryImportRows = [];
        this.deliveryImportFileName = null;
    }

    deliveryImportHeaders(): string[] {
        return this.deliveryImportColumns().map(column => column.key);
    }

    deliveryImportColumns(): DeliveryImportColumn[] {
        return [
            { key: 'driverId', label: 'Driver ID' },
            { key: 'driverResourceCompanyId', label: 'Driver Resource Company ID' },
            { key: 'driverPhoneNumber', label: 'Driver Phone Number' },
            { key: 'vehicleId', label: 'Vehicle ID' },
            { key: 'vehicleResourceCompanyId', label: 'Vehicle Resource Company ID' },
            { key: 'vehicleRegistrationNumber', label: 'Vehicle Registration Number' },
            { key: 'vehiclePlateNumber', label: 'Vehicle Plate Number' },
            ...this.shipmentPayloadItems().map((payloadItem, index) => ({
                key: this.payloadImportColumnKey(payloadItem, index),
                label: this.payloadImportColumnKey(payloadItem, index)
            })),
            ...this.deliveryFlexFieldConfigs.map(config => ({
                key: this.flexImportColumnKey(config),
                label: this.flexImportColumnKey(config)
            }))
        ];
    }

    downloadDeliveryImportTemplate(): void {
        const worksheet = XLSX.utils.json_to_sheet([this.emptyTemplateRow()]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Delivery Assignments');
        XLSX.writeFile(workbook, `delivery-assignment-template-${this.shipment?.shipmentNumber || this.shipmentId}.xlsx`);
    }

    onDeliveryImportFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) {
            return;
        }

        this.deliveryImportFileName = file.name;
        this.deliveryImportLoading = true;
        file.arrayBuffer()
            .then(buffer => {
                const workbook = XLSX.read(buffer, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '', raw: false });
                this.processDeliveryImportRows(rows);
            })
            .catch(error => {
                this.deliveryImportLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Import failed',
                    detail: error?.message || 'Unable to read the delivery assignment file.'
                });
            });
        input.value = '';
    }

    processDeliveryImportRows(rows: Record<string, any>[]): void {
        const parsedRows = rows
            .map((row, index) => this.createDeliveryImportRow(row, index + 2))
            .filter(row => this.hasImportValues(row.values));

        if (!parsedRows.length) {
            this.deliveryImportRows = [];
            this.deliveryImportLoading = false;
            this.messageService.add({ severity: 'warn', summary: 'No rows', detail: 'The selected file does not contain delivery assignment rows.' });
            return;
        }

        forkJoin(parsedRows.map(row => this.resolveDeliveryImportRow(row))).subscribe({
            next: resolvedRows => {
                this.applyAggregatePayloadImportValidation(resolvedRows);
                this.deliveryImportRows = resolvedRows;
                this.deliveryImportLoading = false;
            },
            error: () => {
                this.deliveryImportRows = parsedRows.map(row => ({
                    ...row,
                    status: 'INVALID',
                    errors: [...row.errors, 'Unable to validate import row']
                }));
                this.deliveryImportLoading = false;
            }
        });
    }

    addValidImportedAssignments(): void {
        const validRows = this.deliveryImportRows.filter(row => row.status === 'VALID');
        if (!validRows.length) {
            this.messageService.add({ severity: 'warn', summary: 'No valid rows', detail: 'There are no valid delivery rows to add.' });
            return;
        }

        validRows.forEach(row => {
            const group = this.createAssignmentGroup();
            this.appendDrivers(row.driver ? [row.driver] : []);
            this.appendVehicles(row.vehicle ? [row.vehicle] : []);
            group.patchValue({
                driver: row.driver,
                vehicle: row.vehicle
            });
            row.payloadQuantities.forEach((quantity, index) => {
                (group.get('payloadItems') as FormArray).at(index)?.get('quantity')?.setValue(quantity, { emitEvent: false });
            });
            this.populateFlexFieldArray(group.get('flexFields') as FormArray, row.flexFieldValues);
            this.assignments.push(group);
        });

        this.payloadAllocationsManuallyEdited = true;
        this.deliveryImportDialogVisible = false;
        this.messageService.add({
            severity: 'success',
            summary: 'Assignments added',
            detail: `${validRows.length} delivery assignment row(s) were added for review.`
        });
    }

    deliveryImportValidCount(): number {
        return this.deliveryImportRows.filter(row => row.status === 'VALID').length;
    }

    deliveryImportInvalidCount(): number {
        return this.deliveryImportRows.filter(row => row.status === 'INVALID').length;
    }

    importStatusSeverity(status: DeliveryImportStatus): 'success' | 'danger' {
        return status === 'VALID' ? 'success' : 'danger';
    }

    importPayloadSummary(row: DeliveryImportRow): string {
        const parts = row.payloadQuantities
            .map((quantity, index) => quantity > 0 ? `${this.payloadLineLabel(this.shipmentPayloadItems()[index])}: ${quantity}` : null)
            .filter((part): part is string => !!part);
        return parts.length ? parts.join(' | ') : '-';
    }

    importFlexSummary(row: DeliveryImportRow): string {
        const parts = row.flexFieldValues
            .filter(value => value.value !== null && value.value !== undefined && value.value !== '')
            .map(value => `${value.alternateFieldName || value.fieldName}: ${value.value}`);
        return parts.length ? parts.join(' | ') : '-';
    }

    private initializeSisterScope(): void {
        const applyScope = () => {
            this.sisterCompanyScope = resolveSisterCompanyScope(
                this.tenantScopeService.getAccessSummary(),
                this.currentCompanyId,
                ['fleet.drivers.view', 'fleet.vehicles.view']
            );
            this.applyRouteState(this.route.snapshot.queryParamMap);
            this.loadAssignmentResources();
        };

        this.tenantScopeService.initializeCurrentAccess().subscribe(() => applyScope());
    }

    private applyRouteState(queryParams: ParamMap): void {
        const requestedResourceView = queryParams.get('resourceView');
        this.resourceView = requestedResourceView === 'sister' && this.canUseSisterCompanyMode ? 'sister' : 'current';
        const requestedMemberCompanyId = this.toOptionalNumber(queryParams.get('memberCompanyId'));
        this.selectedSisterCompanyId = this.isValidSisterCompanyId(requestedMemberCompanyId) ? requestedMemberCompanyId : null;
        this.selectedTransporterCrmClientId = this.assignmentSource === 'transporter'
            ? this.toOptionalNumber(queryParams.get('transporterCrmClientId'))
            : null;
    }

    private loadTransporterOptions(): void {
        const companyId = this.currentCompanyId;
        if (!companyId) {
            this.transporterOptions = [];
            return;
        }
        this.transporterOptionsLoading = true;
        this.crmService.getScopedTransporters(companyId).pipe(
            finalize(() => this.transporterOptionsLoading = false)
        ).subscribe({
            next: options => {
                this.transporterOptions = options ?? [];
                if (this.selectedTransporterCrmClientId && !this.transporterOptions.some(option => option.crmClientId === this.selectedTransporterCrmClientId)) {
                    this.selectedTransporterCrmClientId = null;
                }
            },
            error: () => {
                this.transporterOptions = [];
                this.messageService.add({
                    severity: 'error',
                    summary: 'Transporters unavailable',
                    detail: 'Unable to load the transporters available to this company.'
                });
            }
        });
    }

    private syncRouteState(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                source: null,
                isFreelancer: null,
                resourceView: this.resourceView,
                memberCompanyId: this.isSisterMode ? this.selectedSisterCompanyId : null,
                transporterCrmClientId: null
            },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private loadAssignmentResources(): void {
        this.resourceQueryVersion++;
        this.availableDrivers = [];
        this.availableVehicles = [];
        this.vehicleOptionsError = false;
        this.driverAvailabilityByKey.clear();
        this.vehicleAvailabilityByKey.clear();
        this.scopedAvailabilityKey = null;
        this.scopedAvailabilityLoadingKey = null;
        this.driverOptionsLoading = false;
        this.vehicleOptionsLoading = false;
        this.syncLoadingState();
        const queryVersion = this.resourceQueryVersion;
        this.driverOptionsLoading = true;
        this.vehicleOptionsLoading = true;
        this.syncLoadingState();
        forkJoin({ drivers: this.fetchMixedDrivers(), vehicles: this.fetchMixedVehicles() })
            .pipe(finalize(() => {
                if (queryVersion === this.resourceQueryVersion) {
                    this.driverOptionsLoading = false;
                    this.vehicleOptionsLoading = false;
                    this.syncLoadingState();
                }
            }))
            .subscribe({
                next: ({ drivers, vehicles }) => {
                    if (queryVersion !== this.resourceQueryVersion) return;
                    this.availableDrivers = this.normalizeScopedDrivers([...drivers, ...this.availableDrivers]);
                    this.availableVehicles = this.normalizeScopedVehicles([...vehicles, ...this.availableVehicles]);
                },
                error: error => {
                    if (queryVersion !== this.resourceQueryVersion) return;
                    this.vehicleOptionsError = true;
                    this.messageService.add({
                        severity: 'error',
                        summary: error?.status === 403 && this.isSisterMode ? 'Sister-company fleet access denied' : 'Fleet unavailable',
                        detail: error?.status === 403 && this.isSisterMode
                            ? sisterCompanyFleetAccessMessage('both')
                            : 'Unable to load drivers and vehicles for this assignment.'
                    });
                }
            });
        this.ensureScopedAvailability();
    }

    retryFleetOptions(): void {
        if (this.vehicleOptionsError && !this.driverOptionsLoading && !this.vehicleOptionsLoading) {
            this.loadAssignmentResources();
        }
    }

    private pinVehicleOption(vehicle: Vehicle): Vehicle {
        const available = this.availableVehicles.find(option => option?.vehicleId === vehicle.vehicleId);
        if (available) {
            return available;
        }
        const normalized = this.normalizeVehicleOption(vehicle);
        this.availableVehicles = [...this.availableVehicles, normalized];
        return normalized;
    }

    vehicleSelectionMessage(index: number): string | null {
        return this.vehicleSelectionMessages.get(this.assignments.at(index) as FormGroup) ?? null;
    }

    private selectCurrentVehicleForDriver(group: FormGroup, driver: Driver | null): void {
        const version = (this.vehicleSelectionVersions.get(group) ?? 0) + 1;
        this.vehicleSelectionVersions.set(group, version);
        const vehicleControl = group.get('vehicle');
        vehicleControl?.setValue(null, { emitEvent: false });
        this.vehicleSelectionMessages.delete(group);
        if (!driver || group.get('assignmentType')?.value !== 'freelance' || this.isSisterMode) {
            return;
        }

        const vehicleId = Number(driver.currentVehicle?.vehicleId);
        if (!Number.isFinite(vehicleId) || vehicleId <= 0) {
            this.vehicleSelectionMessages.set(group, 'This driver has no current vehicle. Select a vehicle.');
            return;
        }
        const companyId = this.currentCompanyId;
        if (!companyId) {
            return;
        }
        const queryVersion = this.resourceQueryVersion;
        const plate = driver.currentVehicle?.plateNumber || `Vehicle #${vehicleId}`;
        this.vehicleSelectionMessages.set(group, `Checking whether ${plate} is available for this company...`);
        this.vehicleService.findEligibleFreelanceVehicleByCompanyId(vehicleId, companyId).subscribe({
            next: response => {
                if (this.vehicleSelectionVersions.get(group) !== version || queryVersion !== this.resourceQueryVersion
                        || vehicleControl?.value) {
                    return;
                }
                const vehicle = response.success && response.data?.vehicleId === vehicleId ? response.data : null;
                if (vehicle?.resourceCompanyId === companyId
                        && vehicle.resourceOwnershipType?.toUpperCase() === 'FREELANCE'
                        && vehicle.isActive === true) {
                    vehicleControl?.setValue(this.pinVehicleOption(vehicle));
                    this.vehicleSelectionMessages.delete(group);
                } else {
                    this.vehicleSelectionMessages.set(group,
                        `${plate} is not an active freelance vehicle for this company. Select another vehicle or update its fleet classification.`);
                }
            },
            error: () => {
                if (this.vehicleSelectionVersions.get(group) === version && queryVersion === this.resourceQueryVersion
                        && !vehicleControl?.value) {
                    this.vehicleSelectionMessages.set(group, `Could not check ${plate}. Select a vehicle manually.`);
                }
            }
        });
    }

    private fetchMixedDrivers(): Observable<Driver[]> {
        return forkJoin([
            this.fetchAllScopedDrivers('owned'),
            this.fetchAllScopedDrivers('freelance'),
            this.fetchAllScopedDrivers('rental'),
            this.fetchAllTransporterDriversForCompany()
        ]).pipe(map(pages => pages.flat()));
    }

    private fetchMixedVehicles(): Observable<Vehicle[]> {
        return forkJoin([
            this.fetchAllScopedVehicles('owned'),
            this.fetchAllScopedVehicles('freelance'),
            this.fetchAllScopedVehicles('rental'),
            this.fetchAllTransporterVehiclesForCompany()
        ]).pipe(map(pages => pages.flat()));
    }

    private fetchAllScopedDrivers(source: 'owned' | 'freelance' | 'rental', page = 0, collected: Driver[] = []): Observable<Driver[]> {
        return this.fetchScopedDriversPage(page, source).pipe(switchMap(response => {
            const items = response.items ?? [];
            const next = [...collected, ...items];
            return response.hasMore && items.length ? this.fetchAllScopedDrivers(source, page + 1, next) : of(next);
        }));
    }

    private fetchAllScopedVehicles(source: 'owned' | 'freelance' | 'rental', page = 0, collected: Vehicle[] = []): Observable<Vehicle[]> {
        return this.fetchScopedVehiclesPage(page, source).pipe(switchMap(response => {
            const items = response.items ?? [];
            const next = [...collected, ...items];
            return response.hasMore && items.length ? this.fetchAllScopedVehicles(source, page + 1, next) : of(next);
        }));
    }

    private fetchAllTransporterDriversForCompany(): Observable<Driver[]> {
        const companyId = this.currentCompanyId;
        if (!companyId) return of([]);
        return this.crmService.getScopedTransporters(companyId).pipe(
            map(transporters => transporters.filter(transporter => !this.isSisterMode
                || isSisterCompanyResource(transporter.linkedCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId))),
            switchMap(transporters => transporters.length
                ? forkJoin(transporters.map(transporter => this.fetchAllTransporterDrivers(transporter.crmClientId)))
                : of([])),
            map(pages => pages.flat()),
            catchError(() => of([]))
        );
    }

    private fetchAllTransporterVehiclesForCompany(): Observable<Vehicle[]> {
        const companyId = this.currentCompanyId;
        if (!companyId) return of([]);
        return this.crmService.getScopedTransporters(companyId).pipe(
            map(transporters => transporters.filter(transporter => !this.isSisterMode
                || isSisterCompanyResource(transporter.linkedCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId))),
            switchMap(transporters => transporters.length
                ? forkJoin(transporters.map(transporter => this.fetchAllTransporterVehicles(transporter.crmClientId)))
                : of([])),
            map(pages => pages.flat()),
            catchError(() => of([]))
        );
    }

    private fetchAllTransporterDrivers(transporterId: number, page = 0, collected: Driver[] = []): Observable<Driver[]> {
        const companyId = this.currentCompanyId;
        if (!companyId) return of(collected);
        return this.driverService.getTransporterDrivers(page, this.optionPageSize, companyId, transporterId).pipe(
            switchMap(response => {
                const items = response.items ?? [];
                const next = [...collected, ...items];
                return response.hasMore && items.length ? this.fetchAllTransporterDrivers(transporterId, page + 1, next) : of(next);
            }),
            catchError(() => of(collected))
        );
    }

    private fetchAllTransporterVehicles(transporterId: number, page = 0, collected: Vehicle[] = []): Observable<Vehicle[]> {
        const companyId = this.currentCompanyId;
        if (!companyId) return of(collected);
        return this.vehicleService.getTransporterVehicles(page, this.optionPageSize, companyId, transporterId).pipe(
            switchMap(response => {
                const items = response.items ?? [];
                const next = [...collected, ...items];
                return response.hasMore && items.length ? this.fetchAllTransporterVehicles(transporterId, page + 1, next) : of(next);
            }),
            catchError(() => of(collected))
        );
    }

    private fetchScopedDriversPage(page: number, source: 'owned' | 'freelance' | 'rental'): Observable<APIListWrapper<Driver>> {
        const companyId = this.currentCompanyId;
        if (!companyId) {
            return of(this.emptyListWrapper<Driver>());
        }
        if (this.isSisterMode) {
            if (!this.sisterCompanyScope) {
                return of(this.emptyListWrapper<Driver>());
            }
            if (source === 'rental') {
                return this.driverService.getRentalDriversByCompanyGroup(
                    page,
                    this.optionPageSize,
                    this.sisterCompanyScope.companyGroupId,
                    companyId,
                    this.selectedSisterCompanyId
                );
            }
            if (source === 'freelance') {
                return this.driverService.getFreelanceDriversByCompanyGroup(
                    page,
                    this.optionPageSize,
                    this.sisterCompanyScope.companyGroupId,
                    companyId,
                    this.selectedSisterCompanyId
                );
            }
            return this.driverService.findDriversByCompanyGroup(
                page,
                this.optionPageSize,
                this.sisterCompanyScope.companyGroupId,
                companyId,
                this.selectedSisterCompanyId
            );
        }

        if (source === 'rental') {
            return this.driverService.getRentalDriversByCompanyId(page, this.optionPageSize, companyId);
        }
        if (source === 'freelance') {
            return this.driverService.getFreelanceDriversByCompanyId(page, this.optionPageSize, companyId);
        }

        const agencyId = this.sessionStorageService.getCompany()?.transportationAgencyId;
        if (!agencyId) {
            return of(this.emptyListWrapper<Driver>());
        }
        return this.driverService.findDriversByTransportationAgencyId(page, this.optionPageSize, agencyId);
    }

    private fetchScopedVehiclesPage(page: number, source: 'owned' | 'freelance' | 'rental'): Observable<APIListWrapper<Vehicle>> {
        const companyId = this.currentCompanyId;
        if (!companyId) {
            return of(this.emptyListWrapper<Vehicle>());
        }
        if (this.isSisterMode) {
            if (!this.sisterCompanyScope) {
                return of(this.emptyListWrapper<Vehicle>());
            }
            if (source === 'rental') {
                return this.vehicleService.getRentalVehiclesByCompanyGroup(
                    page,
                    this.optionPageSize,
                    this.sisterCompanyScope.companyGroupId,
                    companyId,
                    this.selectedSisterCompanyId
                );
            }
            if (source === 'freelance') {
                return this.vehicleService.getFreelanceVehiclesByCompanyGroup(
                    page,
                    this.optionPageSize,
                    this.sisterCompanyScope.companyGroupId,
                    companyId,
                    this.selectedSisterCompanyId
                );
            }
            return this.vehicleService.findVehiclesByCompanyGroup(
                page,
                this.optionPageSize,
                this.sisterCompanyScope.companyGroupId,
                companyId,
                this.selectedSisterCompanyId
            );
        }

        if (source === 'rental') {
            return this.vehicleService.getRentalVehiclesByCompanyId(page, this.optionPageSize, companyId);
        }
        if (source === 'freelance') {
            return this.vehicleService.getFreelanceVehiclesByCompanyId(page, this.optionPageSize, companyId);
        }

        const agencyId = this.sessionStorageService.getCompany()?.transportationAgencyId;
        if (!agencyId) {
            return of(this.emptyListWrapper<Vehicle>());
        }
        return this.vehicleService.findVehiclesByTransportationAgencyId(page, this.optionPageSize, agencyId);
    }

    private loadScopedAvailability(): Observable<FleetAvailabilityResponse | null> {
        const window = this.mobilizationDateWindow();
        if (this.isSisterMode && this.sisterCompanyScope && this.currentCompanyId) {
            return this.mobilizationService.getAvailabilityByCompanyGroup({
                companyGroupId: this.sisterCompanyScope.companyGroupId,
                excludeCompanyId: this.currentCompanyId,
                memberCompanyId: this.selectedSisterCompanyId,
                dateFrom: window.overlapFrom,
                dateTo: window.overlapTo
            }).pipe(
                map(response => response.data ?? null),
                catchError(() => of(null))
            );
        }
        if (!this.currentCompanyId) {
            return of(null);
        }
        return this.mobilizationService.getAvailability({
            owningCompanyId: this.currentCompanyId,
            dateFrom: window.overlapFrom,
            dateTo: window.overlapTo
        }).pipe(
            map(response => response.data ?? null),
            catchError(() => of(null))
        );
    }

    private ensureScopedAvailability(): void {
        const key = [
            this.resourceView,
            this.selectedSisterCompanyId ?? 'all',
            this.shipment?.pickupTime?.toString() ?? '',
            this.shipment?.dropoffTime?.toString() ?? ''
        ].join(':');
        if (this.scopedAvailabilityKey === key || this.scopedAvailabilityLoadingKey === key) {
            return;
        }

        this.scopedAvailabilityLoadingKey = key;
        this.loadScopedAvailability().subscribe({
            next: availability => {
                if (this.scopedAvailabilityLoadingKey !== key) {
                    return;
                }
                this.applyScopedAvailability(availability);
                this.scopedAvailabilityKey = key;
            },
            error: () => {
                // Availability badges are advisory. A failed availability lookup
                // must not prevent paged resource selection.
            },
            complete: () => {
                if (this.scopedAvailabilityLoadingKey === key) {
                    this.scopedAvailabilityLoadingKey = null;
                }
            }
        });
    }

    private normalizeScopedDrivers(drivers: Driver[]): Driver[] {
        const seen = new Set<string>();
        return drivers
            .filter(driver => !this.isSisterMode || isSisterCompanyResource(driver.resourceCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId))
            .map(driver => this.normalizeDriverOption(driver))
            .filter(driver => {
                const key = driver.tableRowKey || '';
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            })
            .sort((left, right) => this.compareDriverOptions(left, right));
    }

    private normalizeScopedVehicles(vehicles: Vehicle[]): Vehicle[] {
        const seen = new Set<string>();
        return vehicles
            .filter(vehicle => !this.isSisterMode || isSisterCompanyResource(vehicle.resourceCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId))
            .map(vehicle => this.normalizeVehicleOption(vehicle))
            .filter(vehicle => {
                const key = vehicle.tableRowKey || '';
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            })
            .sort((left, right) => this.compareVehicleOptions(left, right));
    }

    private normalizeDriverOption(driver: Driver): Driver {
        const resourceCompanyId = driver.resourceCompanyId ?? this.currentCompanyId ?? undefined;
        const resourceCompanyName = driver.resourceCompanyName ?? this.currentCompanyName ?? undefined;
        return {
            ...driver,
            resourceCompanyId,
            resourceCompanyName,
            optionLabel: driver.legalName || driver.name || `Driver #${driver.id}`,
            searchText: [driver.legalName, driver.name, driver.phoneNumber, driver.licenseNumber, driver.residenceNumber,
                fleetResourceOwnershipSearchText({ ...driver, resourceCompanyName })].filter(Boolean).join(' '),
            ownershipSearchText: fleetResourceOwnershipSearchText({
                ...driver,
                resourceCompanyName
            }),
            tableRowKey: this.driverOptionKeyFromParts(driver.id ?? null, resourceCompanyId ?? null)
        } as Driver & { ownershipSearchText?: string };
    }

    private normalizeVehicleOption(vehicle: Vehicle): Vehicle {
        const resourceCompanyId = vehicle.resourceCompanyId ?? this.currentCompanyId ?? undefined;
        const resourceCompanyName = vehicle.resourceCompanyName ?? this.currentCompanyName ?? undefined;
        return {
            ...vehicle,
            resourceCompanyId,
            resourceCompanyName,
            optionLabel: vehicle.plateNumber || vehicle.registrationNumber || `Vehicle #${vehicle.vehicleId}`,
            searchText: [vehicle.plateNumber, vehicle.registrationNumber, vehicle.engineNumber,
                fleetResourceOwnershipSearchText({ ...vehicle, resourceCompanyName })].filter(Boolean).join(' '),
            ownershipSearchText: fleetResourceOwnershipSearchText({
                ...vehicle,
                resourceCompanyName
            }),
            tableRowKey: this.vehicleOptionKeyFromParts(vehicle.vehicleId ?? null, resourceCompanyId ?? null)
        } as Vehicle & { ownershipSearchText?: string };
    }

    private applyScopedAvailability(availability: FleetAvailabilityResponse | null): void {
        this.driverAvailabilityByKey.clear();
        this.vehicleAvailabilityByKey.clear();

        for (const row of availability?.driverRows ?? []) {
            this.driverAvailabilityByKey.set(
                this.driverOptionKeyFromParts(row.resourceId ?? null, row.resourceCompanyId ?? null),
                row
            );
        }
        for (const row of availability?.vehicleRows ?? []) {
            this.vehicleAvailabilityByKey.set(
                this.vehicleOptionKeyFromParts(row.resourceId ?? null, row.resourceCompanyId ?? null),
                row
            );
        }

        this.availableDrivers = [...this.availableDrivers].sort((left, right) => this.compareDriverOptions(left, right));
        this.availableVehicles = [...this.availableVehicles].sort((left, right) => this.compareVehicleOptions(left, right));
    }

    private appendDrivers(drivers: Driver[]): void {
        const normalized = drivers.map(driver => this.normalizeDriverOption(driver));
        const existing = this.availableDrivers.filter((driver): driver is Driver => !!driver);
        const keys = new Set(existing.map(driver => driver.tableRowKey));
        this.availableDrivers = [...normalized.filter(driver => !keys.has(driver.tableRowKey)), ...existing];
    }

    private appendVehicles(vehicles: Vehicle[]): void {
        const existing = new Set(this.availableVehicles.filter(Boolean).map(vehicle => vehicle.tableRowKey));
        this.availableVehicles = [
            ...vehicles.map(vehicle => this.normalizeVehicleOption(vehicle)).filter(vehicle => !existing.has(vehicle.tableRowKey)),
            ...this.availableVehicles
        ];
    }

    private emptyTemplateRow(): Record<string, string> {
        return this.deliveryImportHeaders().reduce((row, header) => {
            row[header] = '';
            return row;
        }, {} as Record<string, string>);
    }

    private createDeliveryImportRow(values: Record<string, any>, rowNumber: number): DeliveryImportRow {
        const cleanValues = this.cleanImportValues(values);
        const errors: string[] = [];
        const flexFieldValues = this.deliveryFlexFieldConfigs.map(config => this.parseImportFlexField(config, cleanValues, errors));
        const payloadQuantities = this.shipmentPayloadItems().map((payloadItem, index) =>
            this.parseImportQuantity(this.importValue(cleanValues, this.payloadImportColumnKey(payloadItem, index)), this.payloadLineLabel(payloadItem), errors)
        );

        return {
            rowNumber,
            values: cleanValues,
            status: errors.length ? 'INVALID' : 'VALID',
            errors,
            payloadQuantities,
            flexFieldValues
        };
    }

    private resolveDeliveryImportRow(row: DeliveryImportRow): Observable<DeliveryImportRow> {
        return forkJoin({
            driver: this.resolveImportDriver(row.values),
            vehicle: this.resolveImportVehicle(row.values)
        }).pipe(
            map(({ driver, vehicle }) => {
                if (driver.error) {
                    row.errors.push(driver.error);
                }
                if (vehicle.error) {
                    row.errors.push(vehicle.error);
                }
                row.driver = driver.value;
                row.vehicle = vehicle.value;
                if (!row.driver?.id && !driver.error) {
                    row.errors.push('Driver must be registered before assigning deliveries.');
                }
                if (!row.vehicle && !vehicle.error) {
                    row.errors.push('Vehicle was not found');
                }
                row.status = row.errors.length ? 'INVALID' : 'VALID';
                return row;
            }),
            catchError(error => {
                row.errors.push(error?.message || 'Unable to resolve driver or vehicle');
                row.status = 'INVALID';
                return of(row);
            })
        );
    }

    private resolveImportDriver(values: Record<string, string>): Observable<DeliveryImportLookupResult<Driver>> {
        const driverId = this.parseOptionalNumber(this.importValue(values, 'driverId'));
        const resourceCompanyId = this.parseOptionalNumber(this.importValue(values, 'driverResourceCompanyId'));
        const phoneNumber = this.importValue(values, 'driverPhoneNumber');
        const scopedDrivers = this.availableDrivers.filter(driver =>
            (resourceCompanyId == null || driver.resourceCompanyId === resourceCompanyId)
            && (
                (driverId != null && driver.id === driverId)
                || (phoneNumber && this.normalizePhone(driver.phoneNumber) === this.normalizePhone(phoneNumber))
            )
        );

        if (scopedDrivers.length === 1) {
            return of({ value: scopedDrivers[0] });
        }
        if (scopedDrivers.length > 1) {
            return of({ value: null, error: 'Driver match is ambiguous. Provide driverResourceCompanyId.' });
        }
        return of({ value: null });
    }

    private resolveImportVehicle(values: Record<string, string>): Observable<DeliveryImportLookupResult<Vehicle>> {
        const vehicleId = this.parseOptionalNumber(this.importValue(values, 'vehicleId'));
        const resourceCompanyId = this.parseOptionalNumber(this.importValue(values, 'vehicleResourceCompanyId'));
        const registrationNumber = this.importValue(values, 'vehicleRegistrationNumber');
        const plateNumber = this.importValue(values, 'vehiclePlateNumber');
        const normalizedRegistration = this.normalizeLookup(registrationNumber);
        const normalizedPlate = this.normalizeLookup(plateNumber);
        const scopedVehicles = this.availableVehicles.filter(vehicle =>
            (resourceCompanyId == null || vehicle.resourceCompanyId === resourceCompanyId)
            && (
                (vehicleId != null && vehicle.vehicleId === vehicleId)
                || (registrationNumber && this.normalizeLookup(vehicle.registrationNumber) === normalizedRegistration)
                || (plateNumber && this.normalizeLookup(vehicle.plateNumber) === normalizedPlate)
            )
        );

        if (scopedVehicles.length === 1) {
            return of({ value: scopedVehicles[0] });
        }
        if (scopedVehicles.length > 1) {
            return of({ value: null, error: 'Vehicle match is ambiguous. Provide vehicleResourceCompanyId.' });
        }
        return of({ value: null });
    }

    private applyAggregatePayloadImportValidation(rows: DeliveryImportRow[]): void {
        this.shipmentPayloadItems().forEach((payloadItem, payloadIndex) => {
            const importedQuantity = rows
                .filter(row => row.status === 'VALID')
                .reduce((sum, row) => sum + Number(row.payloadQuantities[payloadIndex] ?? 0), 0);
            const existingQuantity = this.assignedQuantityForPayload(payloadIndex);
            const allowedQuantity = this.payloadTotalQuantity(payloadItem);
            if (existingQuantity + importedQuantity <= allowedQuantity) {
                return;
            }
            const message = `Payload allocation exceeds shipment quantity for ${this.payloadLineLabel(payloadItem)}`;
            rows
                .filter(row => Number(row.payloadQuantities[payloadIndex] ?? 0) > 0)
                .forEach(row => {
                    row.errors.push(message);
                    row.status = 'INVALID';
                });
        });
    }

    private populateFlexFieldArray(flexFields: FormArray, values: FlexFieldValue[]): void {
        while (flexFields.length > 0) {
            flexFields.removeAt(0);
        }
        values.forEach(value => {
            flexFields.push(this.fb.group({
                configId: [value.configId ?? null],
                fieldName: [value.fieldName],
                alternateFieldName: [value.alternateFieldName ?? null],
                dataType: [value.dataType ?? null],
                value: [value.value ?? null],
                owningCompanyId: [value.owningCompanyId ?? this.deliveryFlexOwningCompanyId ?? null]
            }));
        });
    }

    private payloadImportColumnKey(payloadItem: PayloadItemView, index: number): string {
        const lineId = payloadItem.packingListIndex ?? index + 1;
        return `Payload ${lineId} Quantity - ${this.payloadLineLabel(payloadItem)}`;
    }

    private flexImportColumnKey(config: FlexFieldConfig): string {
        return `Flex - ${config.alternateFieldName || config.fieldName}`;
    }

    private parseImportFlexField(config: FlexFieldConfig, values: Record<string, string>, errors: string[]): FlexFieldValue {
        const rawValue = this.importValue(values, this.flexImportColumnKey(config));
        const fieldLabel = config.alternateFieldName || config.fieldName;
        let parsedValue: any = rawValue || null;

        if (!rawValue && config.required) {
            errors.push(`${fieldLabel} is required`);
        } else if (rawValue) {
            parsedValue = this.parseFlexFieldValue(rawValue, config, errors);
        }

        return {
            configId: config.configId,
            fieldName: config.fieldName,
            alternateFieldName: config.alternateFieldName,
            dataType: config.dataType,
            owningCompanyId: config.owningCompanyId ?? this.deliveryFlexOwningCompanyId ?? null,
            value: parsedValue
        };
    }

    private parseFlexFieldValue(value: string, config: FlexFieldConfig, errors: string[]): any {
        const fieldLabel = config.alternateFieldName || config.fieldName;
        switch ((config.dataType || '').toUpperCase()) {
            case 'INTEGER': {
                const parsed = Number(value);
                if (!Number.isInteger(parsed)) {
                    errors.push(`${fieldLabel} must be an integer`);
                    return null;
                }
                return parsed;
            }
            case 'DECIMAL': {
                const parsed = Number(value);
                if (!Number.isFinite(parsed)) {
                    errors.push(`${fieldLabel} must be a decimal number`);
                    return null;
                }
                return parsed;
            }
            case 'BOOLEAN': {
                const normalized = this.normalizeLookup(value);
                if (['true', 'yes', 'y', '1'].includes(normalized)) {
                    return true;
                }
                if (['false', 'no', 'n', '0'].includes(normalized)) {
                    return false;
                }
                errors.push(`${fieldLabel} must be yes or no`);
                return null;
            }
            case 'DATE':
                return this.toDateInputValue(value, false, fieldLabel, errors);
            case 'DATETIME':
                return this.toDateInputValue(value, true, fieldLabel, errors);
            default:
                return config.fieldLength && value.length > config.fieldLength
                    ? (errors.push(`${fieldLabel} exceeds ${config.fieldLength} characters`), value)
                    : value;
        }
    }

    private parseImportQuantity(value: string, label: string, errors: string[]): number {
        if (!value) {
            return 0;
        }
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) {
            errors.push(`${label} quantity must be numeric`);
            return 0;
        }
        if (parsed < 0) {
            errors.push(`${label} quantity cannot be negative`);
            return 0;
        }
        return parsed;
    }

    private cleanImportValues(values: Record<string, any>): Record<string, string> {
        return Object.entries(values || {}).reduce((clean, [key, value]) => {
            clean[key.trim()] = value === null || value === undefined ? '' : String(value).trim();
            return clean;
        }, {} as Record<string, string>);
    }

    private hasImportValues(values: Record<string, string>): boolean {
        return Object.values(values).some(value => !!value);
    }

    private importValue(values: Record<string, string>, key: string): string {
        const normalizedKey = this.normalizeHeader(key);
        const entry = Object.entries(values).find(([candidate]) => this.normalizeHeader(candidate) === normalizedKey);
        return entry?.[1]?.trim() ?? '';
    }

    private parseOptionalNumber(value: string): number | null {
        if (!value) {
            return null;
        }
        const parsed = Number(String(value).replace(/\.0$/, ''));
        return Number.isFinite(parsed) ? parsed : null;
    }

    private toDateInputValue(value: string, includeTime: boolean, label: string, errors: string[]): string | null {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            errors.push(`${label} must be a valid ${includeTime ? 'date/time' : 'date'}`);
            return null;
        }
        const pad = (part: number) => part.toString().padStart(2, '0');
        const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
        return includeTime ? `${datePart}T${pad(date.getHours())}:${pad(date.getMinutes())}` : datePart;
    }

    private normalizeHeader(value: string): string {
        return this.normalizeLookup(value).replace(/[^a-z0-9]/g, '');
    }

    private normalizeLookup(value?: string | null): string {
        return (value ?? '').toString().trim().toLowerCase();
    }

    private normalizePhone(value?: string | null): string {
        return (value ?? '').toString().replace(/[^0-9]/g, '');
    }

    private toOptionalNumber(value?: string | null): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private isValidSisterCompanyId(companyId: number | null): boolean {
        return companyId != null && this.sisterCompanies.some(company => company.id === companyId);
    }

    private hasMoreOptions(hasMore: boolean | undefined, receivedCount: number): boolean {
        return hasMore ?? receivedCount === this.optionPageSize;
    }

    private syncLoadingState(): void {
        this.isLoading = this.driverOptionsLoading || this.vehicleOptionsLoading;
    }

    private driverOptionKeyFromParts(driverId: number | null, resourceCompanyId: number | null): string {
        return `${driverId ?? 'none'}:${resourceCompanyId ?? 'none'}`;
    }

    private vehicleOptionKeyFromParts(vehicleId: number | null, resourceCompanyId: number | null): string {
        return `${vehicleId ?? 'none'}:${resourceCompanyId ?? 'none'}`;
    }

    driverAvailability(driver?: Driver | null): FleetAvailabilityRow | undefined {
        if (!driver) {
            return undefined;
        }
        return this.driverAvailabilityByKey.get(
            this.driverOptionKeyFromParts(driver.id ?? null, driver.resourceCompanyId ?? null)
        );
    }

    vehicleAvailability(vehicle?: Vehicle | null): FleetAvailabilityRow | undefined {
        if (!vehicle) {
            return undefined;
        }
        return this.vehicleAvailabilityByKey.get(
            this.vehicleOptionKeyFromParts(vehicle.vehicleId ?? null, vehicle.resourceCompanyId ?? null)
        );
    }

    availabilityStatusLabel(row?: FleetAvailabilityRow | null): string {
        switch (row?.availabilityStatus) {
            case 'FREE':
                return 'Idle';
            case 'BUSY':
                return 'Busy';
            case 'MAINTENANCE_BLOCKED':
                return 'Blocked';
            default:
                return 'Unknown';
        }
    }

    availabilitySeverity(row?: FleetAvailabilityRow | null): 'success' | 'warn' | 'danger' | 'info' {
        switch (row?.availabilityStatus) {
            case 'FREE':
                return 'success';
            case 'BUSY':
                return 'warn';
            case 'MAINTENANCE_BLOCKED':
                return 'danger';
            default:
                return 'info';
        }
    }

    availabilityLocationLine(row?: FleetAvailabilityRow | null): string {
        return row?.availableLocationLabel
            || [row?.availableCityName, row?.availableCountryName].filter(Boolean).join(', ')
            || 'Location unavailable';
    }

    availabilitySummaryLine(row?: FleetAvailabilityRow | null): string {
        if (!row) return 'Availability pending';
        if (row.availabilityStatus === 'FREE') {
            return row.idleSince ? `Idle since ${this.availabilityDateLabel(row.idleSince)}` : 'Available now';
        }
        return row.nextFreeAt
            ? `Next free ${this.availabilityDateLabel(row.nextFreeAt)}`
            : row.availabilityReason || 'Busy on another assignment';
    }

    vehicleTypeLine(vehicle?: Vehicle | null): string {
        return vehicle?.type?.name || 'Vehicle type not set';
    }

    vehicleIdentityLine(vehicle?: Vehicle | null): string {
        return [vehicle?.plateNumber, vehicle?.registrationNumber].filter(Boolean).join(' | ') || 'Identity pending';
    }

    availabilityFifoLine(row?: FleetAvailabilityRow | null): string {
        return row?.fifoRank != null ? `FIFO #${row.fifoRank}` : 'FIFO pending';
    }

    driverCompanyLine(driver?: Driver | null): string {
        return driver?.resourceCompanyName || this.currentCompanyName || '-';
    }

    vehicleCompanyLine(vehicle?: Vehicle | null): string {
        return vehicle?.resourceCompanyName || this.currentCompanyName || '-';
    }

    driverOwnerLine(driver?: Driver | null): string {
        if (!driver) {
            return '-';
        }
        return `${fleetResourceOwnershipTypeLabel(driver)} | ${fleetResourceOwnerLabel(driver)}`;
    }

    vehicleOwnerLine(vehicle?: Vehicle | null): string {
        if (!vehicle) {
            return '-';
        }
        return `${fleetResourceOwnershipTypeLabel(vehicle)} | ${fleetResourceOwnerLabel(vehicle)}`;
    }

    driverAgencySourceLine(driver?: Driver | null): string {
        if (!driver) {
            return '-';
        }
        return [
            fleetResourceAgencyLabel(driver),
            fleetResourceSourceCompanyLabel(driver) || this.driverCompanyLine(driver)
        ].filter(Boolean).join(' | ');
    }

    vehicleAgencySourceLine(vehicle?: Vehicle | null): string {
        if (!vehicle) {
            return '-';
        }
        return [
            fleetResourceAgencyLabel(vehicle),
            fleetResourceSourceCompanyLabel(vehicle) || this.vehicleCompanyLine(vehicle)
        ].filter(Boolean).join(' | ');
    }

    driverOptionLabel(driver?: Driver | null): string {
        if (!driver) {
            return 'Driver';
        }
        return driver.legalName || driver.name || `Driver #${driver.id ?? ''}`.trim();
    }

    vehicleOptionLabel(vehicle?: Vehicle | null): string {
        if (!vehicle) {
            return 'Vehicle';
        }
        return vehicle.plateNumber || vehicle.registrationNumber || `Vehicle #${vehicle.vehicleId ?? ''}`.trim();
    }

    shipmentEquipmentSuggestionLabel(): string | null {
        const equipment = this.shipment?.vehicleParaPack;
        const values = [
            equipment?.vehicleType?.name,
            equipment?.vehicleConfig?.name,
            equipment?.vehicleCapacity?.name
        ].filter((value): value is string => !!value?.trim());
        return values.length ? values.join(' · ') : null;
    }

    isSuggestedVehicle(vehicle?: Vehicle | null): boolean {
        const equipment = this.shipment?.vehicleParaPack;
        if (!vehicle || !equipment || !this.shipmentEquipmentSuggestionLabel()) {
            return false;
        }
        return (!equipment.vehicleType?.id || equipment.vehicleType.id === vehicle.type?.id)
            && (!equipment.vehicleConfig?.configId || equipment.vehicleConfig.configId === vehicle.config?.configId)
            && (!equipment.vehicleCapacity?.capacityId || equipment.vehicleCapacity.capacityId === vehicle.capacity?.capacityId);
    }

    private compareDriverOptions(left: Driver, right: Driver): number {
        return this.compareAvailabilityRowsForOptions(
            this.driverAvailability(left),
            this.driverAvailability(right),
            this.driverOptionLabel(left),
            this.driverOptionLabel(right)
        );
    }

    private compareVehicleOptions(left: Vehicle, right: Vehicle): number {
        const suggestedDelta = Number(this.isSuggestedVehicle(right)) - Number(this.isSuggestedVehicle(left));
        if (suggestedDelta !== 0) {
            return suggestedDelta;
        }
        return this.compareAvailabilityRowsForOptions(
            this.vehicleAvailability(left),
            this.vehicleAvailability(right),
            this.vehicleOptionLabel(left),
            this.vehicleOptionLabel(right)
        );
    }

    private compareAvailabilityRowsForOptions(
        left: FleetAvailabilityRow | undefined,
        right: FleetAvailabilityRow | undefined,
        leftLabel: string,
        rightLabel: string
    ): number {
        const statusDelta = this.availabilityRank(left) - this.availabilityRank(right);
        if (statusDelta !== 0) {
            return statusDelta;
        }
        const fifoDelta = this.fifoRankValue(left) - this.fifoRankValue(right);
        if (fifoDelta !== 0) {
            return fifoDelta;
        }
        return leftLabel.localeCompare(rightLabel);
    }

    private availabilityRank(row?: FleetAvailabilityRow): number {
        switch (row?.availabilityStatus) {
            case 'FREE':
                return 0;
            case 'BUSY':
                return 1;
            case 'MAINTENANCE_BLOCKED':
                return 2;
            default:
                return 3;
        }
    }

    private fifoRankValue(row?: FleetAvailabilityRow): number {
        return row?.fifoRank ?? Number.MAX_SAFE_INTEGER;
    }

    private emptyListWrapper<T>(): APIListWrapper<T> {
        return {
            items: [],
            count: 0,
            hasMore: false,
            limit: 0,
            offset: 0
        } as APIListWrapper<T>;
    }

    private availabilityDateLabel(value?: string | Date | number | null): string {
        if (!value) {
            return '-';
        }
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
    }

    assignmentDriverReadiness(index: number): HrWorkforceReadinessSnapshot | undefined {
        const driver = this.assignments.at(index)?.get('driver')?.value as Driver | null;
        return driver?.id ? this.driverReadinessByDriverId.get(Number(driver.id)) : undefined;
    }

    assignmentDriverReadinessLoading(index: number): boolean {
        const driver = this.assignments.at(index)?.get('driver')?.value as Driver | null;
        return !!driver?.id && this.driverReadinessLoadingByDriverId.has(Number(driver.id));
    }

    assignmentDriverReadinessStatusLabel(index: number): string {
        if (this.assignmentDriverReadinessLoading(index)) {
            return 'Evaluating';
        }
        const snapshot = this.assignmentDriverReadiness(index);
        return snapshot ? this.formatReadiness(snapshot.readinessStatus) : 'Not evaluated';
    }

    assignmentDriverReadinessSummary(index: number): string {
        const driver = this.assignments.at(index)?.get('driver')?.value as Driver | null;
        if (!driver?.id) {
            return 'Select a registered driver to preview HR readiness.';
        }
        if (this.assignmentDriverReadinessLoading(index)) {
            return 'Checking HR employee link, active employment, documents, and driver qualifications.';
        }
        const snapshot = this.assignmentDriverReadiness(index);
        if (snapshot) {
            return `${snapshot.blockerCount ?? 0} blockers, ${snapshot.warningCount ?? 0} warnings. Expires ${this.formatReadinessDate(snapshot.expiresAt)}.`;
        }
        if (this.driverReadinessErrorsByDriverId.has(Number(driver.id))) {
            return 'HR readiness preview is unavailable for this user or tenant.';
        }
        return 'No linked HR readiness snapshot was returned for this driver.';
    }

    assignmentDriverReadinessIssue(index: number): string {
        const snapshot = this.assignmentDriverReadiness(index);
        if (!snapshot) {
            return '';
        }
        const issue = this.readinessRuleResults(snapshot)
            .find(result => result.blocker || result.warning || result.resultStatus === 'FAIL' || result.resultStatus === 'WARN');
        return issue?.message || 'No blocking HR readiness issue is currently visible.';
    }

    readinessSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'READY') return 'success';
        if (status === 'WARNING') return 'warn';
        if (status === 'BLOCKED') return 'danger';
        if (status === 'OVERRIDDEN') return 'info';
        return 'secondary';
    }

    openDriverDialog(): void {
        this.driverDialogVisible = true;
    }

    openVehicleDialog(): void {
        this.vehicleDialogVisible = true;
    }

    openQuickFleetOnboarding(assignmentIndex: number | null = null): void {
        if (this.isSisterMode) {
            this.messageService.add({ severity: 'info', summary: 'Current company only', detail: 'Create the resource in the current company, then return to sister-company assignment.' });
            return;
        }
        this.quickFleetAssignmentIndex = assignmentIndex;
        this.quickFleetOnboardingVisible = true;
    }

    onQuickFleetOnboarded(result: QuickFleetOnboardingResult): void {
        this.quickFleetOnboardingVisible = false;
        const driver = result.driver.resourceOwnershipType
            ? result.driver
            : { ...result.driver, resourceOwnershipType: result.source.toUpperCase() };
        this.appendDrivers([driver]);
        this.appendVehicles([result.vehicle]);
        const assignment = this.quickFleetAssignmentIndex == null
            ? this.assignments.controls.find(control => !control.get('driver')?.value && !control.get('vehicle')?.value)
            : this.assignments.at(this.quickFleetAssignmentIndex);
        if (assignment) {
            assignment.patchValue({
                driver,
                vehicle: result.vehicle,
                transporterCrmClientId: result.transporterCrmClientId ?? null
            });
        }
        this.quickFleetAssignmentIndex = null;
        this.messageService.add({ severity: 'success', summary: 'Driver and vehicle linked', detail: `${this.driverOptionLabel(driver)} is ready with ${result.vehicle.plateNumber}.` });
    }

    openMobilizationDialog(): void {
        this.mobilizationDialogVisible = true;
        this.mobilizationsFirst = 0;
        this.loadMobilizations();
    }

    loadMobilizations(event?: TableLazyLoadEvent): void {
        if (event) {
            this.mobilizationsFirst = event.first ?? 0;
            this.mobilizationsRowsPerPage = event.rows ?? this.mobilizationsRowsPerPage;
        }

        const owningCompanyId = this.deliveryFlexOwningCompanyId;
        if (!owningCompanyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available for mobilization search.' });
            return;
        }

        const page = Math.floor(this.mobilizationsFirst / this.mobilizationsRowsPerPage);
        const dateWindow = this.mobilizationDateWindow();
        const filters: MobilizationSearchParams = {
            owningCompanyId,
            page,
            pageSize: this.mobilizationsRowsPerPage,
            status: 'ACTIVE',
            keyword: this.mobilizationKeyword,
            overlapFrom: dateWindow.overlapFrom,
            overlapTo: dateWindow.overlapTo
        };

        this.mobilizationsLoading = true;
        this.mobilizationService.searchMobilizations(filters)
            .pipe(finalize(() => this.mobilizationsLoading = false))
            .subscribe({
                next: response => {
                    this.mobilizations = response.items ?? [];
                    this.mobilizationsTotalRecords = response.count ?? this.mobilizations.length;
                },
                error: () => {
                    this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load active mobilizations.' });
                }
            });
    }

    onMobilizationKeywordInput(event: Event): void {
        this.mobilizationKeyword = (event.target as HTMLInputElement).value;
    }

    searchMobilizations(): void {
        this.mobilizationsFirst = 0;
        this.loadMobilizations();
    }

    clearMobilizationSearch(): void {
        this.mobilizationKeyword = '';
        this.searchMobilizations();
    }

    selectMobilization(mobilization: Mobilization): void {
        if (!mobilization.driverId || !mobilization.vehicleId) {
            this.messageService.add({ severity: 'error', summary: 'Invalid mobilization', detail: 'Selected mobilization does not have both driver and vehicle.' });
            return;
        }

        const vehicle = this.mapMobilizationVehicle(mobilization);
        const driver = this.mapMobilizationDriver(mobilization, vehicle);
        this.appendDrivers([driver]);
        this.appendVehicles([vehicle]);

        const group = this.createAssignmentGroup();
        group.patchValue({
            mobilizationId: mobilization.mobilizationId ?? null,
            driver,
            vehicle
        });
        this.assignments.push(group);
        this.rebalancePayloadAllocationsIfAutomatic();
        this.mobilizationDialogVisible = false;
    }

    onDriverCreated(driver: Driver): void {
        this.driverDialogVisible = false;
        if (driver?.id) {
            this.appendDrivers([driver]);
            this.patchFirstEmptyAssignment('driver', driver);
        } else {
            this.loadAssignmentResources();
        }
    }

    onVehicleCreated(vehicle: Vehicle): void {
        this.vehicleDialogVisible = false;
        if (vehicle?.vehicleId) {
            this.appendVehicles([vehicle]);
            this.patchFirstEmptyAssignment('vehicle', vehicle);
        } else {
            this.loadAssignmentResources();
        }
    }

    private patchFirstEmptyAssignment(controlName: 'driver' | 'vehicle', value: Driver | Vehicle): void {
        const emptyAssignment = this.assignments.controls.find(control => !control.get(controlName)?.value);
        if (emptyAssignment) {
            emptyAssignment.get(controlName)?.setValue(value);
        }
    }

    private hasUnregisteredDriverAssignment(): boolean {
        return this.assignments.controls.some(control => {
            const driver = control.get('driver')?.value as Driver | null;
            return !!driver && !driver.id;
        });
    }

    private extractFlexFieldValues(flexFields: FormArray): FlexFieldValue[] {
        return flexFields.controls.map(control => control.value as FlexFieldValue);
    }

    getAssignmentPayloadItems(index: number): FormArray {
        return this.assignments.at(index).get('payloadItems') as FormArray;
    }

    shipmentPayloadItems(): PayloadItemView[] {
        return this.shipment?.payloadItemList ?? [];
    }

    payloadLineLabel(payloadItem: PayloadItemView): string {
        return payloadItem.itemDesciption || payloadItem.itemType?.name || `Payload line ${payloadItem.packingListIndex ?? ''}`.trim();
    }

    payloadQuantityLabel(payloadItem: PayloadItemView): string {
        return `${this.payloadTotalQuantity(payloadItem)} ${payloadItem.itemQuantity?.uom?.name || ''}`.trim();
    }

    payloadWeightLabel(payloadItem: PayloadItemView): string {
        const totalWeight = this.payloadTotalWeight(payloadItem);
        return `${this.formatNumber(totalWeight)} ${payloadItem.weightUnit?.shortName || ''}`.trim();
    }

    allocationWeightLabel(assignmentIndex: number, payloadIndex: number): string {
        const group = this.getAssignmentPayloadItems(assignmentIndex).at(payloadIndex);
        const quantity = Number(group.get('quantity')?.value ?? 0);
        const unitWeight = Number(group.get('unitWeight')?.value ?? 0);
        return `${this.formatNumber(quantity * unitWeight)} ${group.get('weightUnitName')?.value || ''}`.trim();
    }

    assignedQuantityForPayload(payloadIndex: number): number {
        return this.existingAssignedQuantityForPayload(payloadIndex) + this.formAssignedQuantityForPayload(payloadIndex);
    }

    private formAssignedQuantityForPayload(payloadIndex: number): number {
        return this.assignments.controls.reduce((sum, assignment) => {
            const payloadItems = assignment.get('payloadItems') as FormArray;
            return sum + Number(payloadItems?.at(payloadIndex)?.get('quantity')?.value ?? 0);
        }, 0);
    }

    private existingAssignedQuantityForPayload(payloadIndex: number): number {
        const shipmentPayloadItem = this.shipmentPayloadItems()[payloadIndex];
        return this.allocatedDeliveries().reduce((sum, delivery) => {
            return sum + (delivery.payloadItemList ?? []).reduce((deliverySum, payloadItem, deliveryPayloadIndex) => {
                if (!this.payloadItemsMatch(shipmentPayloadItem, payloadItem, payloadIndex, deliveryPayloadIndex)) {
                    return deliverySum;
                }
                return deliverySum + this.payloadTotalQuantity(payloadItem);
            }, 0);
        }, 0);
    }

    remainingQuantityForPayload(payloadIndex: number): number {
        const payloadItem = this.shipmentPayloadItems()[payloadIndex];
        return Math.max(0, this.payloadTotalQuantity(payloadItem) - this.assignedQuantityForPayload(payloadIndex));
    }

    maxQuantityForAllocation(assignmentIndex: number, payloadIndex: number): number {
        const current = Number(this.getAssignmentPayloadItems(assignmentIndex).at(payloadIndex).get('quantity')?.value ?? 0);
        const payloadItem = this.shipmentPayloadItems()[payloadIndex];
        const existingQuantity = this.existingAssignedQuantityForPayload(payloadIndex);
        const assignedInOtherRows = this.formAssignedQuantityForPayload(payloadIndex) - current;
        return Math.max(0, this.payloadTotalQuantity(payloadItem) - existingQuantity - assignedInOtherRows);
    }

    onPayloadQuantityChange(assignmentIndex: number, payloadIndex: number): void {
        const control = this.getAssignmentPayloadItems(assignmentIndex).at(payloadIndex).get('quantity');
        if (!control) {
            return;
        }
        this.payloadAllocationsManuallyEdited = true;
        const value = Math.max(0, Number(control.value ?? 0));
        const max = this.maxQuantityForAllocation(assignmentIndex, payloadIndex);
        const clamped = Math.min(value, max);
        if (clamped !== control.value) {
            control.setValue(clamped, { emitEvent: false });
        }
    }

    payloadAllocationSummary(): string {
        const payloads = this.shipmentPayloadItems();
        if (!payloads.length) {
            return 'No shipment payload lines available.';
        }
        return payloads
            .map((payload, index) => `${this.payloadLineLabel(payload)}: ${this.assignedQuantityForPayload(index)} / ${this.payloadTotalQuantity(payload)} ${payload.itemQuantity?.uom?.name || ''}`.trim())
            .join(' | ');
    }

    private createPayloadAllocationArray(): FormArray {
        return this.fb.array(this.shipmentPayloadItems().map(payloadItem => this.createPayloadAllocationGroup(payloadItem)));
    }

    private createPayloadAllocationGroup(payloadItem: PayloadItemView): FormGroup {
        return this.fb.group({
            shipmentLineId: [payloadItem.shipmentLineId ?? null],
            packingListIndex: [payloadItem.packingListIndex ?? null],
            itemDescription: [payloadItem.itemDesciption ?? payloadItem.itemType?.name ?? null],
            itemTypeName: [payloadItem.itemType?.name ?? null],
            quantity: [0, [Validators.min(0)]],
            maxQuantity: [this.payloadTotalQuantity(payloadItem)],
            uomName: [payloadItem.itemQuantity?.uom?.name ?? payloadItem.itemQuantity?.uom?.shortName ?? ''],
            unitWeight: [Number(payloadItem.itemWeight ?? 0)],
            weightUnitName: [payloadItem.weightUnit?.shortName ?? '']
        });
    }

    private rebalancePayloadAllocations(): void {
        const assignmentCount = this.assignments.length;
        if (!assignmentCount) {
            return;
        }
        this.shipmentPayloadItems().forEach((payloadItem, payloadIndex) => {
            const availableQuantity = Math.max(0, this.payloadTotalQuantity(payloadItem) - this.existingAssignedQuantityForPayload(payloadIndex));
            const baseQuantity = Math.floor(availableQuantity / assignmentCount);
            let remainder = availableQuantity % assignmentCount;
            this.assignments.controls.forEach(assignment => {
                const payloadItems = assignment.get('payloadItems') as FormArray;
                const quantity = baseQuantity + (remainder > 0 ? 1 : 0);
                remainder = Math.max(0, remainder - 1);
                payloadItems.at(payloadIndex)?.get('quantity')?.setValue(quantity, { emitEvent: false });
            });
        });
    }

    equalSplitPayloadAllocations(): void {
        if (!this.assignments.length) {
            this.messageService.add({ severity: 'warn', summary: 'No assignments', detail: 'Add at least one assignment before splitting payload.' });
            return;
        }
        this.rebalancePayloadAllocations();
        this.payloadAllocationsManuallyEdited = false;
        this.messageService.add({ severity: 'success', summary: 'Payload split', detail: 'Payload quantities were split equally across assignments.' });
    }

    private rebalancePayloadAllocationsIfAutomatic(): void {
        if (!this.payloadAllocationsManuallyEdited) {
            this.rebalancePayloadAllocations();
        }
    }

    private clampPayloadAllocationsToAvailable(): void {
        this.assignments.controls.forEach((assignment, assignmentIndex) => {
            const payloadItems = assignment.get('payloadItems') as FormArray;
            payloadItems.controls.forEach((payloadControl, payloadIndex) => {
                const control = payloadControl.get('quantity');
                if (!control) {
                    return;
                }
                const current = Math.max(0, Number(control.value ?? 0));
                const max = this.maxQuantityForAllocation(assignmentIndex, payloadIndex);
                const clamped = Math.min(current, max);
                if (clamped !== control.value) {
                    control.setValue(clamped, { emitEvent: false });
                }
            });
        });
    }

    private buildAssignedPayloadItems(control: any): PayloadItemView[] {
        const payloadItems = control.get('payloadItems') as FormArray;
        return payloadItems.controls
            .map((payloadControl, index) => {
                const quantity = Number(payloadControl.get('quantity')?.value ?? 0);
                if (quantity <= 0) {
                    return null;
                }
                const source = this.shipmentPayloadItems()[index];
                return {
                    ...source,
                    itemDescription: source.itemDesciption || source.itemType?.name,
                    itemQuantity: {
                        ...(source.itemQuantity ?? {}),
                        quantity
                    },
                    itemWeight: source.itemWeight
                } as PayloadItemView & { itemDescription?: string };
            })
            .filter((payloadItem): payloadItem is PayloadItemView => payloadItem !== null);
    }

    private sanitizeAssignmentDriver(driver: Driver | null | undefined, currentVehicle: Vehicle | null): Driver | null {
        if (!driver) {
            return null;
        }
        const {
            ownershipSearchText,
            tableRowKey,
            currentVehicle: _currentVehicle,
            ...cleanDriver
        } = driver as Driver & { ownershipSearchText?: string; tableRowKey?: string };
        return {
            ...cleanDriver,
            currentVehicle: currentVehicle ?? undefined
        } as Driver;
    }

    private sanitizeAssignmentVehicle(vehicle: Vehicle | null | undefined): Vehicle | null {
        if (!vehicle) {
            return null;
        }
        const {
            ownershipSearchText,
            tableRowKey,
            currentDriver: _currentDriver,
            ...cleanVehicle
        } = vehicle as Vehicle & { ownershipSearchText?: string; tableRowKey?: string; currentDriver?: Driver };
        return cleanVehicle as Vehicle;
    }

    private validatePayloadAllocations(): boolean {
        const exceededLine = this.shipmentPayloadItems().find((payloadItem, index) =>
            this.assignedQuantityForPayload(index) > this.payloadTotalQuantity(payloadItem)
        );
        if (exceededLine) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: `Payload allocation exceeds shipment quantity for ${this.payloadLineLabel(exceededLine)}.`
            });
            return false;
        }
        return true;
    }

    private payloadTotalQuantity(payloadItem?: PayloadItemView): number {
        return Math.max(0, Number(payloadItem?.itemQuantity?.quantity ?? 0));
    }

    private payloadTotalWeight(payloadItem?: PayloadItemView): number {
        return this.payloadTotalQuantity(payloadItem) * Number(payloadItem?.itemWeight ?? 0);
    }

    private requestedTruckCount(): number {
        const requestedTrucks = Number(this.shipment?.quantityRequired ?? 0);
        return Number.isFinite(requestedTrucks) && requestedTrucks > 0 ? Math.floor(requestedTrucks) : 0;
    }

    private assignmentCount(): number {
        return (this.deliveryForm?.get('assignments') as FormArray | null)?.length ?? 0;
    }

    private existingDeliveryCount(): number {
        return this.allocatedDeliveries().length;
    }

    private allocatedDeliveries(): ShipmentDelivery[] {
        return (this.shipment?.deliveries ?? []).filter(delivery => this.isAllocatedDelivery(delivery));
    }

    private isAllocatedDelivery(delivery?: ShipmentDelivery | null): boolean {
        return delivery?.deliveryType === ShipmentDeliveryType.DELIVERY ||
            delivery?.deliveryType === ShipmentDeliveryType.JOB;
    }

    private payloadItemsMatch(shipmentPayloadItem: PayloadItemView | undefined, deliveryPayloadItem: PayloadItemView | undefined, shipmentPayloadIndex: number, deliveryPayloadIndex: number): boolean {
        const shipmentKeys = this.payloadAllocationKeys(shipmentPayloadItem);
        const deliveryKeys = this.payloadAllocationKeys(deliveryPayloadItem);
        if (shipmentKeys.length > 0 && deliveryKeys.length > 0) {
            return shipmentKeys.some(key => deliveryKeys.includes(key));
        }
        return shipmentKeys.length === 0 && deliveryKeys.length === 0 && shipmentPayloadIndex === deliveryPayloadIndex;
    }

    private payloadAllocationKeys(payloadItem: PayloadItemView | undefined): string[] {
        const keys: string[] = [];
        if (payloadItem?.shipmentLineId != null) {
            keys.push(`line:${payloadItem.shipmentLineId}`);
        }
        if (payloadItem?.packingListIndex != null) {
            keys.push(`packing:${payloadItem.packingListIndex}`);
        }
        return keys;
    }

    private pluralize(count: number, singular: string): string {
        return count === 1 ? singular : `${singular}s`;
    }

    private formatNumber(value: number): string {
        return Number.isInteger(value) ? value.toString() : value.toFixed(2);
    }

    mobilizationProjectLabel(row: Mobilization): string {
        if (row.projectSource === 'LOGISTICS_PROJECT') {
            return row.logisticsProjectId ? `Logistics Project #${row.logisticsProjectId}` : 'Logistics project not set';
        }
        return [row.manualProjectCode, row.manualProjectName].filter(Boolean).join(' - ') || 'Manual project';
    }

    mobilizationSiteLabel(row: Mobilization): string {
        return row.manualSiteName || row.manualSiteAddress || 'Site not set';
    }

    private mapMobilizationDriver(mobilization: Mobilization, vehicle: Vehicle): Driver {
        return {
            id: mobilization.driverId!,
            name: mobilization.driverName || `Driver #${mobilization.driverId}`,
            phoneNumber: mobilization.driverPhoneNumber || '',
            licenseNumber: mobilization.driverLicenseNumber || '',
            currentVehicle: vehicle
        } as Driver;
    }

    private mapMobilizationVehicle(mobilization: Mobilization): Vehicle {
        return {
            vehicleId: mobilization.vehicleId!,
            plateNumber: mobilization.vehiclePlateNumber || mobilization.vehicleRegistrationNumber || `Vehicle #${mobilization.vehicleId}`,
            registrationNumber: mobilization.vehicleRegistrationNumber || '',
            type: mobilization.vehicleTypeName ? { name: mobilization.vehicleTypeName } : undefined
        } as Vehicle;
    }

    private evaluateAssignmentDriverReadiness(driver?: Driver | null): void {
        if (!driver?.id) {
            return;
        }
        const driverId = Number(driver.id);
        if (!Number.isFinite(driverId) || this.driverReadinessByDriverId.has(driverId) || this.driverReadinessLoadingByDriverId.has(driverId)) {
            return;
        }

        this.driverReadinessErrorsByDriverId.delete(driverId);
        this.driverReadinessLoadingByDriverId.add(driverId);
        this.hrService.evaluateReadiness({
            driverId,
            assignmentType: 'SHIPMENT_DELIVERY',
            requiredWorkerCategory: 'DRIVER',
            assignmentResourceId: Number(this.shipmentId),
            sourceModule: 'SHIPMENT_DELIVERY',
            sourceResourceId: Number(this.shipmentId),
            forceRefresh: false
        }).pipe(finalize(() => this.driverReadinessLoadingByDriverId.delete(driverId)))
            .subscribe({
                next: (response: { data?: { snapshots?: HrWorkforceReadinessSnapshot[] } }) => {
                    const snapshot = response.data?.snapshots?.[0];
                    if (snapshot) {
                        this.driverReadinessByDriverId.set(driverId, snapshot);
                    }
                },
                error: () => this.driverReadinessErrorsByDriverId.add(driverId)
            });
    }

    private readinessRuleResults(snapshot: HrWorkforceReadinessSnapshot): HrWorkforceReadinessRuleResult[] {
        if (!snapshot.ruleResultsJson) {
            return [];
        }
        try {
            const parsed = JSON.parse(snapshot.ruleResultsJson);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private formatReadiness(value?: string): string {
        return (value || '').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ') || '-';
    }

    private formatReadinessDate(value?: string): string {
        if (!value) {
            return '-';
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
    }

    private mobilizationDateWindow(): { overlapFrom?: Date; overlapTo?: Date } {
        const start = this.toValidDate(this.shipment?.pickupTime) ?? this.toValidDate(this.shipment?.requestedBookingTime);
        const end = this.toValidDate(this.shipment?.dropoffTime);
        const fallback = start ?? end;
        return {
            overlapFrom: start ?? fallback,
            overlapTo: end ?? fallback
        };
    }

    private toValidDate(value?: Date | number | string): Date | undefined {
        if (!value) {
            return undefined;
        }
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }

    generateDeliveries() {
        if (this.assignments.length === 0) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please add at least one assignment.' });
            return;
        }

        if (this.hasUnregisteredDriverAssignment()) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Driver must be registered before assigning deliveries.' });
            return;
        }

        if (this.deliveryForm.invalid) {
            const missingCashBox = this.assignments.controls.some(control => control.get('tripMoney')?.hasError('cashBoxRequired'));
            this.messageService.add({ severity: 'error', summary: 'Error', detail: missingCashBox
                ? 'Choose the custodian cash box for each driver cash request.'
                : 'Please fill in all required fields for each assignment.' });
            return;
        }

        if (!this.validatePayloadAllocations()) {
            return;
        }
        const containerIds = this.assignments.controls.flatMap(control => control.get('containerCaseIds')?.value ?? []);
        if (new Set(containerIds).size !== containerIds.length) {
            this.messageService.add({ severity: 'error', summary: 'Duplicate container', detail: 'Assign each container to only one delivery.' });
            return;
        }

        this.deliveryGenerationInProgress = true;

        const deliveryAssignments = this.assignments.controls.map(control => {
            const assignment = control.value;
            const vehicle = this.sanitizeAssignmentVehicle(assignment.vehicle);
            const driver = this.sanitizeAssignmentDriver(assignment.driver, vehicle);
            return {
                mobilizationId: assignment.mobilizationId,
                containerCaseIds: assignment.containerCaseIds,
                transporterCrmClientId: assignment.assignmentType === 'transporter'
                    ? assignment.transporterCrmClientId
                    : null,
                driver,
                vehicle,
                transportCostAmount: assignment.transportCostAmount,
                transportCostCurrencyCode: assignment.transportCostCurrencyCode || this.shipment?.currencyCode || 'SAR',
                waybillNumber: assignment.waybillNumber?.trim() || undefined,
                tripMoney: {
                    enabled: assignment.tripMoney?.enabled !== false,
                    applyRateCardDefault: true,
                    payMethod: assignment.tripMoney?.payMethod || 'NONE',
                    ratePerKm: assignment.tripMoney?.ratePerKm ?? undefined,
                    distanceSource: assignment.tripMoney?.distanceSource,
                    expectedStartPlace: assignment.tripMoney?.expectedStartPlace?.trim() || undefined,
                    expectedParkingPlace: assignment.tripMoney?.expectedParkingPlace?.trim() || undefined,
                    payTripGroupKey: assignment.tripMoney?.payTripGroupKey?.trim() || undefined,
                    tripEarningAmount: assignment.tripMoney?.tripEarningAmount ?? undefined,
                    cashPurpose: assignment.tripMoney?.cashPurpose || 'NONE',
                    tripCashAmount: assignment.tripMoney?.tripCashAmount ?? undefined,
                    pettyCashBoxId: assignment.tripMoney?.cashPurpose === 'NONE' ? undefined
                        : assignment.tripMoney?.pettyCashBoxId ?? undefined,
                    currencyCode: assignment.tripMoney?.currencyCode || this.shipment?.currencyCode || 'SAR',
                    reason: assignment.tripMoney?.reason?.trim() || undefined
                },
                laborSelections: (assignment.laborSelections ?? [])
                    .filter((selection: any) => !!selection.fieldWorkerId)
                    .map((selection: any) => ({
                        fieldRoleId: selection.fieldRoleId,
                        slotNumber: selection.slotNumber,
                        fieldWorkerId: selection.fieldWorkerId,
                        buyRateSnapshot: selection.buyRateSnapshot ?? undefined,
                        billingUnit: selection.buyRateSnapshot == null ? undefined : selection.billingUnit,
                        currencyCode: selection.buyRateSnapshot == null ? undefined : selection.currencyCode,
                        overrideReason: selection.buyRateSnapshot == null ? undefined : selection.overrideReason?.trim() || undefined
                    })),
                payloadItemList: this.buildAssignedPayloadItems(control),
                flexFieldValues: this.extractFlexFieldValues(control.get('flexFields') as FormArray)
            };
        });

        this.shipmentService.generateDeliveriesFromAssignments(this.shipmentId, deliveryAssignments).subscribe(() => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Deliveries generated successfully.' });
            this.deliveryGenerationInProgress = false;
            this.router.navigate(['/shipment/view', this.shipmentId]);
        }, error => {
            const detail = typeof error?.error === 'string'
                ? error.error
                : error?.error?.message || error?.error?.error || error?.message || 'Error generating deliveries.';
            this.messageService.add({ severity: 'error', summary: 'Unable to generate deliveries', detail });
            this.deliveryGenerationInProgress = false;
        });
    }

    clearSelections() {
        this.assignments.clear();
        this.driverReadinessByDriverId.clear();
        this.driverReadinessLoadingByDriverId.clear();
        this.driverReadinessErrorsByDriverId.clear();
        this.payloadAllocationsManuallyEdited = false;
    }
}
